// import { Note, ... } from '../../_service/data.service';
// src/app/edit/edit-component.ts
// This file implements the edit note page. It is based on Angular forms and PrimeNG components.
import {
  Directive,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  AfterViewInit,
  Renderer2,
  HostListener,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Location } from '@angular/common';
import { Router, CanDeactivate } from '@angular/router';
import { ChangeDetectorRef } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ValidationErrors,
  ValidatorFn,
  FormRecord,
} from '@angular/forms';
import { forkJoin, firstValueFrom, Subscription, Subject, Observable } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { CanComponentDeactivate } from '../../_guards/unsaved-changes.guard';

import { MenuItem, MessageService, ConfirmationService } from 'primeng/api';

import { LayoutService } from '../../_service/layout.service';

import {
  Note,
  Tag,
  TagMutationPayload,
  DataService,
  ApiResponse,
  SimilarNoteResult,
  SuggestedTagResult,
  AiSummary,
  NoteEditSessionSnapshot,
  NoteEditSessionSnapshotContent,
} from '../../_service/data.service';
//import { OLD__UserSettingsService } from '../../_service/OLD_usersettings.service';
import { NavigationService } from '../../_service/navigation.service';
import { SnackbarService } from '../../_service/snackbar.service';
import { Attachment, FileUploadService } from '../../_service/file-upload.service';
import {
  extractAttachmentImageIds,
  isAttachmentReferencedInHtml,
  removeAttachmentImagesFromHtml,
  replaceAttachmentIdInHtml,
} from '../../_util/attachment-ref.util';
import { AttachmentImageInsertPayload } from '../rich-editor/extensions/phase1-image';
import { NoteActionsService } from '../../_service/note-actions.service';
import { AlertService, AlertType } from '../../_service/alert.service'; // Adjust path as needed
import { UserSettingsService } from '../../_service/user-settings.service';
import { DialogType, GlobalDialogService } from '../../_service/global-dialog.service';
import { EnvironmentService } from '../../_service/sync/environment.service';
import { ConnectivityDisplay, resolveConnectivityDisplay } from '../home/logic/sync-state.logic';
import { EditSessionService } from '../../_service/edit-session.service';
import { SyncCoordinatorService } from '../../_service/sync/sync-coordinator.service';
import { SpaceContextService } from '../../_service/space-context.service';
import { Space, SpaceService } from '../../_service/space.service';
import { ScopedTagsService } from '../../_service/scoped-tags.service';
import {
  TAG_SCOPE,
  TagScope,
  TagSuggestionGroup,
  filterPrivateTags,
  groupTagsByScope,
  isPrivateTag,
  resolveNoteSpaceTags,
  hasCurrentTagScope,
  TAG_TYPE_OPTIONS,
  DEFAULT_TAG_TYPE,
} from '../../_util/tag-scope.util';
import {
  DEFAULT_NOTE_STATUS,
  NOTE_PRIORITIES,
  StatusOption,
  effectiveStatus,
  isActionRequired,
  isEmphasizedStatus,
  isMutedStatus,
  isStruckStatus,
  normalizePriority,
  notePriorityValidator,
  noteStatusValidator,
  optionalDateValidator,
  priorityIndicator,
  statusOptionsFor,
  toOptionalDate,
  toOptionalIsoDate,
} from '../../_util/note-status.util';
import { extractInlineTasksFromHtml, mergeInlineTasks } from '../../_util/inline-task.util';
import { UnsavedChangesStateService } from '../../_service/unsaved-changes-state.service';
import { formatPersistedNoteSize } from '../../_util/note-size.util';
import { NoteSizeService } from '../../_service/note-size.service';
import { getOrderedRelatedNoteIds, toRelatedNotesPayload } from '../../_util/related-notes.util';
import { extractSnapshotContent } from '../../_util/note-snapshot.util';
import { AiSessionService } from '../../_service/ai-session.service';

import { RichEditorComponent } from '../rich-editor/rich-editor.component';
import { FileUploadComponent } from '../file-upload/file-upload.component';

// import type Delta from 'quill-delta';
// import Delta from 'quill-delta';
import Delta from 'quill-delta';

interface NewNoteResponse {
  message: string;
  id: string; // Changed from number to string as per your requirement
}

interface SuggestedTagSelection {
  _id: string;
  name: string;
  description: string;
  selected: boolean;
}

interface SimilarNoteSelectionItem {
  _id?: string;
  id?: string;
  title?: string;
  Status?: string;
  updated_at?: string;
  modified_at?: string;
  modifiedDate?: string;
  last_modified?: string;
  final_score?: number;
  semantic_score?: number;
  selected: boolean;
  [key: string]: unknown;
}

/**
 * Shared edit-mode logic for the Edit Note page, NoteEditComponent (`/edit/:_id`), which extends this
 * class and supplies its own template, styles, imports and providers (component metadata isn't
 * inherited).
 * @Directive() (no selector) lets Angular apply this base class's constructor DI, queries and host
 * listeners to the subclass.
 */
@Directive()
export abstract class NoteEditBase implements OnInit, OnDestroy, CanComponentDeactivate {
  @ViewChild('red-button') myButton!: ElementRef;
  @ViewChild('TitleField') TitleField!: ElementRef<HTMLInputElement>;
  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef<HTMLDivElement>;
  //   @ViewChild(FileUploadComponent) fileUploadComponent!: FileUploadComponent;
  @ViewChild(FileUploadComponent, { static: false }) fileUploadComponent!: FileUploadComponent;
  @ViewChild('descriptionEditor') descriptionEditor!: RichEditorComponent;
  @ViewChildren(RichEditorComponent) richEditors?: QueryList<RichEditorComponent>;
  /** Images inserted into the text this session (Insert Image / drag & drop), shown in the Attachments
   * tab right away — the server's list only has them once the note is saved. */
  insertedImageAttachments: Attachment[] = [];
  //layoutService: LayoutService = inject(LayoutService);
  /** Routes used for "View" / "open another note in Edit". */
  protected readonly viewRoute: string = 'view';
  protected readonly editRoute: string = '/edit';
  note!: Note;
  noteId: string = '';
  statusValues: string[] | undefined;
  /** Status dropdown: the current statuses only, plus the note's own legacy status while it still has
   * it (shown, not selectable). Rebuilt whenever the form is (re)initialised. */
  statusOptions: StatusOption[] = statusOptionsFor(null);
  readonly priorityOptions = [...NOTE_PRIORITIES];

  /** Tooltip of the status dropdown: what the selected status means (or that it's an old one). */
  get statusSelectTitle(): string | null {
    const value = this.editNoteForm?.controls['Status']?.value;
    return this.statusOptions.find((o) => o.value === value)?.title ?? null;
  }
  readonly priorityIndicator = priorityIndicator;
  types: string[] | undefined;

  /** Spaces the current user may assign this note to (tasks/space_core_client.md, Stage 2 §5):
   * ACTIVE Spaces where the user's role is OWNER, ADMIN or EDITOR — a VIEWER couldn't save a note
   * into a Space they can't write to. Own PRIVATE Space first, then alphabetical. */
  spaceOptions: Space[] = [];
  /** Same Spaces as spaceOptions, as {id, label} for the Space <p-select> — its own PRIVATE Space
   * is labelled "Private" rather than whatever name the server gave it. */
  spaceSelectItems: Array<{ id: string; label: string }> = [];
  /** The user's own PRIVATE Space id, once known — the default for a note with no spaceId of its
   * own (e.g. one created before Spaces existed). */
  private privateSpaceId: string | null = null;

  editNoteForm!: FormGroup;
  exitOnSaveFlag: boolean = false;
  isHeaderVisible: boolean = false;
  isMetadataMoreVisible: boolean = false;
  isDescriptionExpanded: boolean = true;
  isFileAttachmentsExpanded: boolean = false;
  isTagsExpanded: boolean = false;
  isKeywordsExpanded: boolean = false;
  isCommentsExpanded: boolean = false;
  areAllCollapsibleExpanded: boolean = true;
  isFavorite: boolean = false;
  favoriteNoteIds: string[] = [];
  MobileMenuIsVisible: boolean = false;
  isContentChanged: boolean = false;
  isInitializing: boolean = true; // Flag to prevent change detection during initialization
  loadingCounter = 0; // initially count of loading objects in progress
  private contentChangeCounter = 0;
  isSaving = false;
  /** Set the instant Close is clicked (autosave mode), until the page navigates away. */
  isClosing = false;
  tempMessage: string = '';
  statusMessage: string = '';
  editorZoomFactor: number = 150;
  private routeParamSub?: Subscription;
  private activeEditSessionNoteId: string | null = null;

  temporaryStatus: boolean = false; // Will be set to true if loaded Note has not been saved yet (='Temp')
  /** The Space field is editable only for a brand-new ('Temp') note until the user's first explicit
   * save (Save, Ctrl/Cmd+S, Close, View, …); silent autosaves don't lock it, and meanwhile keep the
   * note a local-only draft (Note.localDraft) so it isn't uploaded into a Space the user may still
   * change. An existing note always opens with it read-only — moving a note between Spaces is a
   * separate, not-yet-built operation (a plain update can't do it: the server resolves the target
   * database from spaceId). */
  isSpaceEditable = false;

  filter: any; // DEBUG- copied from view.component.ts; not implemented!
  homeState: { view?: string; calendarMonth?: string } | null = null;
  menuItems: MenuItem[] = [];
  /** Every tag this note may use: spaceTagCatalog + privateTagCatalog (tasks/frontend-tagging.md). */
  availableTags: Tag[] = [];
  filteredTagSuggestions: Tag[] = [];
  /** The picker's suggestions, grouped Space tags / Private tags. */
  tagSuggestionGroups: TagSuggestionGroup[] = [];
  /** The note's SPACE tags — exactly what goes into Note.Tags on save. */
  noteTags: Tag[] = [];
  /** The current user's PRIVATE tags on this note. Saved immediately through the private-tag-assignment
   * API, never through Note.Tags or Note save/sync, so they never reach the shared Note. */
  privateNoteTags: Tag[] = [];
  private privateTagIds: string[] = [];
  /** SPACE tags of this note's Space only — never another Space's. */
  spaceTagCatalog: Tag[] = [];
  /** The current user's own PRIVATE tags (includes every pre-Phase-1 tag, migrated as PRIVATE). */
  privateTagCatalog: Tag[] = [];
  readonly TAG_SCOPE = TAG_SCOPE;
  /** Scope for a tag created from the "Assign tag" input or dialog when the note offers a choice. */
  newTagScope: TagScope = TAG_SCOPE.SPACE;
  readonly tagScopeOptions = [
    { label: 'Space tag', value: TAG_SCOPE.SPACE, icon: 'pi pi-users' },
    { label: 'Private tag', value: TAG_SCOPE.PRIVATE, icon: 'pi pi-lock' },
  ];
  /** tasks/space-move-client.md — "Move to Space" dialog state. */
  moveDialogVisible = false;
  moveDestinationSpaceId: string | null = null;
  isMoving = false;
  /** Scope of the tag open in the Tag Details dialog; null while creating a new one. */
  editingTagScope: TagScope | null = null;
  quickTagName: string | Tag = '';
  filteredParentTagSuggestions: Tag[] = [];
  readonly tagTypeOptions: string[] = [...TAG_TYPE_OPTIONS];
  readonly defaultTagType = DEFAULT_TAG_TYPE;
  tagDialogVisible = false;
  isTagMutating = false;
  editingTagIndex: number | null = null;
  tagDraftId = '';
  tagDraftRev: string | undefined = undefined;
  tagDraftName = '';
  tagDraftType = this.defaultTagType;
  tagDraftSlug = '';
  tagDraftColor = '#2563EB';
  tagDraftIcon = '';
  tagDraftParentTagId = '';
  tagDraftParentTag: string | Tag = '';
  tagDraftSortOrder: number | null = null;
  tagDraftIsActive = true;
  aiKeywordsLoading = false;
  aiSuggestTagsLoading = false;
  aiSummaryLoading = false;
  aiSuggestTagsDialogVisible = false;
  aiSuggestedTags: SuggestedTagSelection[] = [];
  saveTemplateDialogVisible = false;
  templateName = '';

  // Related Notes state
  relatedNotes: string[] = [];
  isRelatedNotesExpanded = false;
  addRelatedNoteDialogVisible = false;
  addRelatedNoteCreateBacklink = true;
  relatedNoteSearchQuery = '';
  relatedNoteSearchResults: Note[] = [];
  similarForLinkDialogVisible = false;
  similarForLinkLoading = false;
  similarForLinkResults: SimilarNoteSelectionItem[] = [];
  similarForLinkError = '';
  createBacklinksChecked = true;
  removeRelatedNoteDialogVisible = false;
  removeRelatedNotePendingIndex = -1;
  removeRelatedNotePendingId = '';
  removeRelatedNotePendingTitle = '';
  removeRelatedNoteDeleteBacklink = true;
  removeRelatedNoteHasBacklink = false;

  similarNotesDialogVisible = false;
  similarNotesLoading = false;
  similarNotesResults: SimilarNoteResult[] = [];
  similarNotesError = '';
  templateNameTouched = false;
  isSavingTemplate = false;
  isOnline: boolean = true; // Tracks online/offline status from EnvironmentService
  // Whether the server actually responded to the last sync attempt — distinct from
  // isOnline, which only reflects the browser's network interface. See SyncStateLogic.
  serverReachable: boolean = true;
  get connectivity(): ConnectivityDisplay {
    return resolveConnectivityDisplay(this.isOnline, this.serverReachable);
  }
  private offlineSaveDialogShown = false;
  private destroy$ = new Subject<void>();
  unsavedChangesDialogVisible = false;
  pendingCancelEdits = false;
  private deactivateResolver: ((value: boolean) => void) | null = null;

  // ---- Autosave (Stage 1) ----
  private static readonly AUTOSAVE_DEBOUNCE_MS = 5000;
  private static readonly AUTOSAVE_MAX_INTERVAL_MS = 25000;
  autosaveEnabled = true;
  // Footer save/sync lifecycle. 'clean' = opened/no edits and already synced; 'saved' = edited, saved
  // locally AND synced. 'saved-local' = saved locally, sync still pending. Offline is not a stored
  // state: it is derived from connectivity in autosaveStatusView.
  //
  // 'saved' ("Saved & synced") is a transient confirmation: it settles back to 'clean' after
  // SAVED_CONFIRMATION_MS. The accessor owns that timer so ANY other state change (a new edit, sync
  // going pending/failed, a note reload) cancels it automatically; nothing else needs to remember to.
  private static readonly SAVED_CONFIRMATION_MS = 5000;
  private savedConfirmationTimer: ReturnType<typeof setTimeout> | null = null;
  private autosaveStateValue: 'clean' | 'unsaved' | 'saving-local' | 'saved-local' | 'syncing' | 'sync-failed' | 'save-failed' | 'saved' = 'clean';

  get autosaveState(): typeof this.autosaveStateValue {
    return this.autosaveStateValue;
  }

  set autosaveState(next: typeof this.autosaveStateValue) {
    if (next === this.autosaveStateValue) return; // re-asserting 'saved' must not restart the countdown
    this.clearSavedConfirmationTimer();
    this.autosaveStateValue = next;
    if (next === 'saved') {
      this.savedConfirmationTimer = setTimeout(() => {
        this.savedConfirmationTimer = null;
        // Only settle if still exactly "saved & synced" with nothing pending in the editor.
        if (this.autosaveStateValue === 'saved' && !this.isContentChanged && !this.autosaveInFlight) {
          this.autosaveStateValue = 'clean';
          this.cdRef.markForCheck();
        }
      }, NoteEditBase.SAVED_CONFIRMATION_MS);
    }
  }

  private clearSavedConfirmationTimer(): void {
    if (this.savedConfirmationTimer) {
      clearTimeout(this.savedConfirmationTimer);
      this.savedConfirmationTimer = null;
    }
  }
  private autosaveDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private autosaveMaxTimer: ReturnType<typeof setTimeout> | null = null;
  private autosaveInFlight = false;
  private autosavePendingRerun = false;
  private autosaveLastSavedGeneration = 0;
  private autosaveSyncSub?: Subscription;

  // ---- Autosave (Stage 2 — session snapshot & discard) ----
  private openingSnapshot: NoteEditSessionSnapshot | null = null;
  /** Set once this edit page is torn down — a snapshot whose save completes after that is removed again. */
  private editSessionEnded = false;
  private openingSnapshotBaselineCounter = 0;
  private discardInvokedViaClose = false;
  /** Dropdown model for the footer's Close SplitButton (autosave mode only). */
  closeSplitMenuItems: MenuItem[] = [
    {
      label: 'Discard changes since opening…',
      icon: 'pi pi-history',
      disabled: true,
      command: () => this.openDiscardChangesDialog(true),
    },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private data_service: DataService,
    private location: Location,
    private cdRef: ChangeDetectorRef,
    private fb: FormBuilder,
    private snackbarService: SnackbarService,
    private messages: MessageService,
    //private userSettingsService: OLD__UserSettingsService,
    private userSettings: UserSettingsService,
    private layoutService: LayoutService, // 👈 add this
    private navigationService: NavigationService,
    private titleService: Title,
    private renderer: Renderer2,
    private alertService: AlertService,
    private gDS: GlobalDialogService,
    private elRef: ElementRef,
    private environmentService: EnvironmentService,
    private confirmationService: ConfirmationService,
    private editSessionService: EditSessionService,
    private unsavedChangesState: UnsavedChangesStateService,
    private aiSessionService: AiSessionService,
    private syncCoordinator: SyncCoordinatorService,
    private spaceContext: SpaceContextService,
    private spaceService: SpaceService,
    private scopedTags: ScopedTagsService,
    // Last on purpose: the lightweight specs construct NoteEditBase positionally (see edit.component.spec.ts).
    private noteActions: NoteActionsService,
    // After noteActions for the same reason; optional because those specs don't pass it.
    private fileUploadService?: FileUploadService,
    private noteSizes?: NoteSizeService,
  ) {}

  openAskNotes(): void {
    const formValue = this.editNoteForm?.value;
    const descriptionHtml = formValue?.DescriptionHtml ?? this.note?.DescriptionHtml ?? '';
    const commentsHtml = formValue?.CommentsHtml ?? this.note?.CommentsHtml ?? '';

    this.aiSessionService.openDialog({
      currentNote: {
        noteId: this.note?._id,
        title: this.note?.Title,
        descriptionHtml,
        commentsHtml,
      },
    });
  }

  // ##############################################################################
  // INITIATION & DATA LOADING FUNCTIONS
  // ##############################################################################

  ngOnInit(): void {
    console.log('######In EditCopmponent -- ngOnInit()###########');
    this.loadingCounter = 0; // Start loading
    this.initializePageMenu();
    const navState = this.router.getCurrentNavigation()?.extras?.state ?? (history.state || {});
    this.filter = navState['filter'] ?? this.filter;
    this.homeState = navState['homeState'] ?? null;
    this.userSettings.loadSavedTheme();
    this.userSettings.userSettings$.subscribe((settings) => {
      console.log('[UserSettingsService] initialize(): loaded', settings);
      if (settings) {
        console.log('primaryColor from stream =', settings.primaryColor);
        if (settings.primaryColor) {
          this.layoutService.appState.update((state: any) => ({
            ...state,
            primary: settings.primaryColor,
          }));
        }
        const zoom = settings.editorZoomFactor ?? this.editorZoomFactor;
        this.editorZoomFactor = zoom;
        document.documentElement.style.setProperty('--fontScaleFactor', `${zoom}%`);
        document.documentElement.style.setProperty('--fontScaleRatio', `${zoom / 100}`);
        // document.documentElement.style.setProperty('--systemZoom', `${settings.systemZoomFactor}%`);
        // document.documentElement.style.setProperty('--systemZoom', `${200}%`);
      }
      this.autosaveEnabled = typeof settings?.autosaveEnabled === 'boolean' ? settings.autosaveEnabled : true;
      this.syncDiscardMenuItemState();
      this.favoriteNoteIds = this.normalizeFavoriteNoteIds(settings?.favoriteNoteIds);
      this.updateFavoriteState();
    });
    this.userSettings.editorZoomFactor$.subscribe((factor) => {
      if (typeof factor === 'number' && !Number.isNaN(factor)) {
        this.editorZoomFactor = factor;
        document.documentElement.style.setProperty('--fontScaleFactor', `${factor}%`);
        document.documentElement.style.setProperty('--fontScaleRatio', `${factor / 100}`);
      }
    });

    // A queued attachment (offline / unsaved note) got its server id: the sync already rewrote the
    // stored note; patch this editor's copy too, so a later save doesn't write back the temporary id.
    this.fileUploadService?.attachmentIdReplaced$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ oldId, newId }) => this.applyAttachmentIdReplacement(oldId, newId));

    // Subscribe to network status changes
    this.environmentService.online$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.isOnline = status;
        if (status) {
          this.offlineSaveDialogShown = false;
        }
        this.updateAiMenuItems();
        this.cdRef.markForCheck();
      });

    // Server reachability - distinct from browser online/offline (see SyncStateLogic):
    // the browser can report "online" while the app's server is unreachable.
    this.syncCoordinator.serverReachable$
      .pipe(takeUntil(this.destroy$))
      .subscribe((reachable) => {
        this.serverReachable = reachable;
        this.cdRef.markForCheck();
      });

    // Refresh relatedNotes when a background sync updates IndexedDB,
    // but only if the user hasn't made unsaved changes yet.
    this.data_service.dataReloaded$
      .pipe(
        filter((v) => v !== null),
        filter(() => !!this.noteId && !this.isContentChanged),
        switchMap(() => this.data_service.getNote(this.noteId!)),
        filter((result) => result.status === 'success' && !!result.data),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        const note = result.data!;
        const rawRelated = Array.isArray((note as any).RelatedNotes)
          ? (note as any).RelatedNotes
          : Array.isArray((note as any).relatedNotes)
            ? (note as any).relatedNotes
            : [];
        const fresh = getOrderedRelatedNoteIds(rawRelated);
        if (fresh.join(',') !== this.relatedNotes.join(',')) {
          this.relatedNotes = fresh;
          this.refreshRelatedNoteObjects();
          this.cdRef.markForCheck();
        }
      });

    this.loadSpaceOptions();

    this.routeParamSub = this.route.paramMap.subscribe((params) => {
      const nextNoteId = String(params.get('_id'));

      if (this.activeEditSessionNoteId && this.activeEditSessionNoteId !== nextNoteId) {
        this.editSessionService.unregisterEditSession(this.activeEditSessionNoteId);
        this.activeEditSessionNoteId = null;
        void this.deleteOpeningSnapshotIfAny(); // the previous note's session ends here
      }

      this.noteId = nextNoteId;
      this.registerCurrentEditSession();
      console.log('param id = ', this.noteId);
      this.loadData(this.noteId);
    });
  }

  onZoomFactorChange(type: 'system' | 'editor', event: any): void {
    if (type === 'editor') {
      console.log('Editor zoom factor changed to ', this.editorZoomFactor);
      this.userSettings.setEditorZoomFactor(this.editorZoomFactor);
    }
  }

  persistEditorZoom(): void {
    this.userSettings.setEditorZoomFactor(this.editorZoomFactor);
  }

  initializePageMenu() {
    this.menuItems = [
      {
        label: 'View item',
        icon: 'fas fa-eye mr-2 !text-red-500',
        styleClass: 'text-black m-2 full-hover', // Use a CSS class instead
        command: () => this.pageMenu('View'),
      },
      {
        label: 'User-Settings',
        icon: 'fas fa-cog mr-2 !text-blue-500',
        styleClass: 'text-black hover:!bg-blue-600 hover:text-white m-2', // Use a CSS class instead
        command: () => this.pageMenu('UserSettings'),
      },
      {
        label: 'Download PDF',
        icon: 'fas fa-file-pdf mr-2 !text-red-500',
        styleClass: 'text-black hover:!bg-blue-600 hover:text-white m-2', // Use a CSS class instead
        command: () => this.pageMenu('DownloadPDF'),
      },
      {
        label: 'Print',
        icon: 'fas fa-print mr-2 !text-blue-500',
        styleClass: 'text-black hover:!bg-blue-600 hover:text-white m-2', // Use a CSS class instead
        command: () => this.pageMenu('Print'),
      },
      {
        label: 'Save as Template...',
        icon: 'fas fa-copy mr-2 !text-blue-500',
        styleClass: 'text-black hover:!bg-blue-600 hover:text-white m-2',
        command: () => this.pageMenu('SaveAsTemplate'),
      },
      { separator: true }, // 🔹 Separator before "Close"
      { label: '––––––––––––––––', disabled: true, styleClass: 'text-gray-500' }, // ✅ Custom separator
      {
        label: 'DEBUG - show raw data',
        icon: 'fa-solid fa-bug-slash mr-2 !text-red-500',
        styleClass: 'text-black hover:!bg-blue-600 hover:text-white m-2', // Use a CSS class instead
        command: () => this.pageMenu('ShowRawData'),
      },
      {
        label: 'New note',
        icon: 'fa-solid fa-plus mr-2 !text-red-500',
        styleClass: 'text-black hover:!bg-blue-600 hover:text-white m-2', // Use a CSS class instead
        command: () => this.pageMenu('New'),
      },
      {
        label: 'Create copy of note...',
        icon: 'pi pi-copy mr-2 !text-blue-500',
        styleClass: 'text-black hover:!bg-blue-600 hover:text-white m-2',
        command: () => this.pageMenu('CreateCopy'),
      },
      {
        label: 'Move to Space...',
        icon: 'pi pi-arrow-right-arrow-left mr-2 !text-blue-500',
        styleClass: 'text-black hover:!bg-blue-600 hover:text-white m-2',
        command: () => this.pageMenu('MoveToSpace'),
      },
      {
        label: 'Delete note',
        icon: 'fa-solid fa-trash mr-2 !text-red-500',
        styleClass: 'text-black hover:!bg-blue-600 hover:text-white m-2', // Use a CSS class instead
        command: () => this.pageMenu('Delete'),
      },
      { label: '––––––––––––––––', disabled: true, styleClass: 'text-gray-500' }, // ✅ Custom separator

      {
        label: 'Find Similar Notes',
        icon: 'pi pi-sparkles mr-2',
        styleClass: 'text-black hover:!bg-blue-600 hover:text-white m-2',
        command: () => this.pageMenu('FindSimilar'),
      },
      {
        label: 'Discard changes since opening…',
        icon: 'pi pi-history mr-2 !text-orange-500',
        styleClass: 'text-black hover:!bg-blue-600 hover:text-white m-2',
        visible: false,
        disabled: true,
        command: () => this.openDiscardChangesDialog(false),
      },
      {
        label: 'Close',
        icon: 'fas fa-window-close mr-2 !text-red-500',
        styleClass: 'text-black hover:!bg-blue-600 hover:text-white m-2', // Use a CSS class instead
        command: () => this.pageMenu('Close'),
      },
    ];
    this.syncDiscardMenuItemState();
  }

  /** Keeps both "Discard changes since opening…" entries' visible/disabled
   * state current (the page menu item and the Close SplitButton's dropdown
   * item) — visible only when autosave is on, enabled only once content
   * actually differs from the opening snapshot. Mirrors the existing
   * updateAiMenuItems() pattern below: p-menu/p-splitButton re-read item
   * properties each time they render, so mutating them in place is enough —
   * no need to rebuild either array. */
  private syncDiscardMenuItemState(): void {
    const canDiscard = this.canDiscardSinceOpening;

    const pageMenuItem = this.menuItems.find((m) => m.label === 'Discard changes since opening…');
    if (pageMenuItem) {
      pageMenuItem.visible = this.autosaveEnabled;
      pageMenuItem.disabled = !canDiscard;
    }

    if (this.closeSplitMenuItems[0]) {
      this.closeSplitMenuItems[0].disabled = !canDiscard;
    }
  }

  private updateAiMenuItems(): void {
    const aiLabels = ['Find Similar Notes'];
    for (const item of this.menuItems) {
      if (item.label && aiLabels.includes(item.label)) {
        item.disabled = !this.isOnline;
        item.title = !this.isOnline ? 'AI features are only available in online mode' : undefined;
      }
    }
  }

  /** Populates spaceOptions (tasks/space_core_client.md, Stage 2 §5) — called once from
   * ngOnInit(), independent of which note is loaded. If the note already on screen has no spaceId
   * of its own, backfills the default (the user's own PRIVATE Space) into both the form and the
   * note once it's known; harmless to skip if the note already has a spaceId (the common case for
   * anything created since Stage 2). */
  private loadSpaceOptions(): void {
    this.spaceService.getSpaces().subscribe({
      next: (spaces) => {
        this.spaceOptions = spaces
          .filter(
            (s) =>
              s.status === 'ACTIVE' &&
              (s.current_user_role === 'OWNER' || s.current_user_role === 'ADMIN' || s.current_user_role === 'EDITOR'),
          )
          .sort((a, b) => {
            if (a.type !== b.type) return a.type === 'PRIVATE' ? -1 : 1;
            return a.name.localeCompare(b.name);
          });
        this.spaceSelectItems = this.spaceOptions.map((s) => ({
          id: s.id,
          label: s.type === 'PRIVATE' ? 'Private' : s.name,
        }));
        this.privateSpaceId = this.spaceOptions.find((s) => s.type === 'PRIVATE')?.id ?? null;
        void this.reloadSpaceTagCatalog();

        // Re-assert the selection (not just when unset): a plain conditional patch would leave the
        // dropdown showing nothing selected if this resolves before initializeEditNoteForm() has
        // even run, or if the note's own spaceId arrived before spaceSelectItems existed to render
        // a matching entry for it. Doing this unconditionally, on every load, is harmless either way.
        const desired = this.note?.spaceId || this.privateSpaceId || undefined;
        if (desired) {
          if (this.note && !this.note.spaceId) {
            this.note.spaceId = desired;
          }
          this.editNoteForm?.get('spaceId')?.setValue(desired);
        }
        this.cdRef.markForCheck();
      },
      error: (err) => {
        console.error('Failed to load Spaces for the Space field:', err);
      },
    });
  }

  loadData(noteIdOverride?: string): void {
    console.log('Edit-component: in loadData()');

    this.statusValues = this.data_service.getAllStatuses();
    this.types = this.data_service.getAllNoteTypes();

    this.loadingCounter++;

    const noteId = noteIdOverride ?? this.noteId ?? String(this.route.snapshot.paramMap.get('_id'));
    const now = new Date().toISOString();

    forkJoin({
      noteResp: this.data_service.getNote(noteId),
    }).subscribe({
      next: ({ noteResp }) => {
        console.log('Edit-component loadData: forkJoin finished. Response:', noteResp);

        let note: Note;
        this.isSpaceEditable = false;

        /* ============================================================
         NOTE HANDLING (existing OR new temp note)
         ============================================================ */

        if (noteResp.status === 'success' && noteResp.data) {
          // ✅ Existing note
          note = noteResp.data;
        } else if (noteId.startsWith('note:')) {
          // ✅ NEW TEMP NOTE (created but not yet persisted)
          console.warn('🆕 Note not found in DB — initializing temporary note:', noteId);

          note = {
            _id: noteId,
            type: 'note',

            Title: '',
            DescriptionDelta: new Delta(),
            DescriptionHtml: '',
            CommentsDelta: new Delta(),
            CommentsHtml: '',

            Keywords: '',
            AiKeywords: '',

            Status: 'Temp',
            NoteType: 'To Do',

            Project: 16,
            ProjectName: '',

            Confidential: false,
            ReferenceDate: now,

            Attachments: [],

            // The currently active Space (tasks/space_core_client.md, Stage 2). This is the code
            // path actually exercised by every "New Note" entry point: each just uses
            // DataOfflineService.createNote()'s returned _id to route here — the note object it
            // built, spaceId included, is otherwise discarded, since nothing has been persisted
            // yet for getNote() (above) to find.
            spaceId: undefined, // new notes default to the user's PRIVATE Space (backfilled in the editor; omitted = PRIVATE server-side)

            created_on: now,
            updated_on: now,
            last_modified: now,
          } as Note;

          this.temporaryStatus = true;
          this.isHeaderVisible = true;
        } else {
          // ❌ Invalid / missing note
          const msg = noteResp.error?.message || `Note not found for ID ${noteId}`;
          this.tempMessage = msg;
          console.warn('⚠️', msg);
          this.alertService?.warn?.(msg, { life: 4000 });
          return;
        }

        // Render from the already-loaded tag cache instantly; refresh from the
        // server in the background (see refreshTagCatalogInBackground) rather
        // than blocking note rendering on a live network round-trip.
        const ownTags: Tag[] = Array.isArray(this.data_service.allTags) ? [...this.data_service.allTags] : [];

        /* ============================================================
         NORMALIZE NOTE FIELDS
         ============================================================ */

        const projects = this.data_service.getAllProjects();
        note.ProjectName = projects.find((p) => p.ID === note.Project)?.Name ?? 'Unknown Project';

        if (!note.ReferenceDate) {
          note.ReferenceDate = note.created_on;
        }

        if (note.Confidential == null) {
          note.Confidential = false;
        }

        if (!note.NoteType || String(note.NoteType).trim() === '') {
          note.NoteType = 'To Do';
        }

        // A draft left behind by an earlier session (tab closed before any explicit save) is still new.
        this.isSpaceEditable = !!note.localDraft;
        if (note.Status === 'Temp') {
          this.temporaryStatus = true;
          this.isSpaceEditable = true;
          this.isHeaderVisible = true;
          note.Status = DEFAULT_NOTE_STATUS;
        }

        note.Attachments = Array.isArray(note.Attachments) ? note.Attachments : [];
        this.privateTagCatalog = filterPrivateTags(ownTags);
        this.spaceTagCatalog = this.scopedTags.getCachedSpaceTags(note.spaceId || this.privateSpaceId);
        this.rebuildTagCatalog();
        this.noteTags = this.resolveSpaceNoteTags(note);
        this.privateTagIds = this.isSpaceEditable ? [] : (this.scopedTags.getCachedPrivateTagIds(note._id) ?? []);
        this.refreshPrivateNoteTags();
        const rawRelated = Array.isArray((note as any).RelatedNotes)
          ? (note as any).RelatedNotes
          : Array.isArray((note as any).relatedNotes)
            ? (note as any).relatedNotes
            : [];
        this.relatedNotes = getOrderedRelatedNoteIds(rawRelated);
        this.refreshRelatedNoteObjects();

        /* ============================================================
         ASSIGN + BUILD UI STATE
         ============================================================ */

        // A different note: forget the previous note's live attachment count so the badge doesn't
        // briefly show it for the new note before FileUploadComponent re-resolves (it won't
        // re-fetch, and so won't re-emit, for the same note id — see its lastLoadedNoteId guard).
        if (this.note?._id !== note._id) {
          this.resolvedAttachmentCount = null;
        }

        this.note = note;
        this.updateFavoriteState();

        this.initializeEditNoteForm();
        // Normalize this.note to exactly match the freshly-initialized form
        // (a no-op today, nothing has been edited yet) so the opening
        // snapshot below is built from the same fields/shape a later
        // autosave would write, via the same existing code path.
        this.applyChangesToNoteFromForm();

        this.setTitle('Edit: ' + (note.Title || 'New Note'));
        this.editSessionService.registerEditSession(note._id, {
          route: `/edit/${encodeURIComponent(note._id)}`,
          title: note.Title || undefined,
        });
        this.openingSnapshot = null;
        this.openingSnapshotBaselineCounter = this.contentChangeCounter;
        if (this.autosaveEnabled) {
          void this.createOpeningSnapshot();
        }

        this.cdRef.detectChanges();
        this.setContentChanged(false);
        this.isInitializing = false;

        // Footer status on open: synced note -> "No changes · Synced"; a note still waiting to be
        // pushed (e.g. edited offline earlier) -> "Saved locally · Sync pending", resolved by the watcher.
        const openedPendingSync = !!note.syncStatus && note.syncStatus !== 'synced';
        this.autosaveState = openedPendingSync ? 'saved-local' : 'clean';
        if (openedPendingSync && this.autosaveEnabled) {
          this.watchForSyncCompletion();
        }

        void this.refreshTagCatalogInBackground();
      },

      error: (err: any) => {
        console.error('❌ Edit-component: loadData() failed', err);
        this.alertService?.error?.('Failed to load note or tags.', { life: 5000 });
      },

      complete: () => {
        this.loadingCounter--;
      },
    });
  }

  initializeEditNoteForm(): void {
    this.statusOptions = statusOptionsFor(this.note.Status);
    // Initialize the form group after note is assigned
    this.editNoteForm = this.fb.group({
      Title: [this.note.Title],
      // Canonical format (Delta)
      DescriptionDelta: [this.note.DescriptionDelta ?? new Delta(), Validators.required],
      CommentsDelta: [this.note.CommentsDelta ?? new Delta()],

      // Derived format (HTML)
      DescriptionHtml: [this.note.DescriptionHtml ?? ''],
      CommentsHtml: [this.note.CommentsHtml ?? ''],

      //Description: [this.note.Description, Validators.required],
      //Comments: [this.note.Comments],
      Status: [this.note.Status, [Validators.required, noteStatusValidator(this.note.Status)]],
      // Status metadata (tasks/frontend-note-status-metadata-requirements.md): missing on older or
      // offline notes, so they default here — actionRequired false, priority Normal, no dates.
      // An (old or new) Action Required status implies the flag, as the backend applies on write.
      actionRequired: [isActionRequired(this.note)],
      priority: [normalizePriority(this.note.priority), notePriorityValidator],
      dueDate: [toOptionalDate(this.note.dueDate), optionalDateValidator],
      completedOn: [toOptionalDate(this.note.completedOn), optionalDateValidator],
      ReferenceDate: [this.note.ReferenceDate ? new Date(this.note.ReferenceDate) : new Date(this.note.created_on)],
      StartDate: [this.note.StartDate ? new Date(this.note.StartDate) : null],
      EndDate: [this.note.EndDate ? new Date(this.note.EndDate) : null],
      Confidential: [this.note.Confidential ?? false],
      NoteType: [this.note.NoteType ?? 'To Do', Validators.required],
      // Defaults to the user's own PRIVATE Space (tasks/space_core_client.md, Stage 2 §5) — only
      // relevant for a note with no spaceId of its own yet, since privateSpaceId isn't known until
      // loadSpaceOptions()'s request resolves (see there for the backfill once it does).
      spaceId: [{ value: this.note.spaceId ?? this.privateSpaceId ?? undefined, disabled: !this.isSpaceEditable }],
      //Attachments: [this.note.Attachments ?? []], // ✅ always defined

      Tags: [this.noteTags.map((tag) => tag._id)],
      Keywords: [this.note.Keywords],
      AiKeywords: [(this.note as any).AiKeywords ?? ''],
    });
  }

  ngOnDestroy(): void {
    // Leaving Edit by any route (back, View, Home, another note…) ends the session normally: its
    // opening snapshot is only for "Discard changes since opening" while the page is open, and must
    // not be left behind for the startup "abandoned session" check (autosave already saved the note).
    this.editSessionEnded = true;
    void this.deleteOpeningSnapshotIfAny();
    this.clearSavedConfirmationTimer();
    this.unsavedChangesState.setHasUnsavedChanges(false);
    if (this.activeEditSessionNoteId) {
      this.editSessionService.unregisterEditSession(this.activeEditSessionNoteId);
      this.activeEditSessionNoteId = null;
    }
    if (this.autosaveDebounceTimer) {
      clearTimeout(this.autosaveDebounceTimer);
      this.autosaveDebounceTimer = null;
    }
    if (this.autosaveMaxTimer) {
      clearTimeout(this.autosaveMaxTimer);
      this.autosaveMaxTimer = null;
    }
    this.routeParamSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private registerCurrentEditSession(): void {
    const noteId = (this.noteId || '').trim();
    if (!noteId) {
      return;
    }

    this.editSessionService.registerEditSession(noteId, {
      route: `/edit/${encodeURIComponent(noteId)}`,
      title: this.note?.Title || undefined,
    });
    this.activeEditSessionNoteId = noteId;
  }

  // ##############################################################################
  // PRESENTATION LAYER / GUI ROUTINES (CALL-BACK ETC.)
  // ##############################################################################

  get f() {
    return this.editNoteForm.controls;
  }

  get isEventType(): boolean {
    const value = this.editNoteForm?.get('NoteType')?.value;
    return String(value || '').trim().toLowerCase() === 'event';
  }

  /** Note + all its attachments (see NoteSizeService). */
  get noteSizeLabel(): string {
    return formatPersistedNoteSize(this.noteSizes ? this.noteSizes.totalSizeBytes(this.note) : this.note?.Size);
  }

  /** Set from FileUploadComponent's (countChange) once it resolves the note's real attachment
   * list (which can come from the backend/IndexedDB independently of note.Attachments — see
   * FileUploadComponent.loadAttachments()). Preferred over the note.Attachments-based estimate
   * below once known, since that field can be empty/stale even when attachments do exist for this
   * note. */
  resolvedAttachmentCount: number | null = null;

  get attachmentCount(): number {
    if (this.resolvedAttachmentCount !== null) {
      return this.resolvedAttachmentCount;
    }
    const attachments = this.note?.Attachments;
    return Array.isArray(attachments) ? attachments.length : 0;
  }

  get tagCount(): number {
    return (Array.isArray(this.noteTags) ? this.noteTags.length : 0) + this.privateNoteTags.length;
  }

  toggleMetadataMore(): void {
    this.isMetadataMoreVisible = !this.isMetadataMoreVisible;
  }

  isLoading(): boolean {
    return this.loadingCounter > 0;
  }

  pageMenu(item: string): void {
    const actions: { [key: string]: () => void } = {
      Close: () => void this.forceSaveThenClose(true),
      Delete: () => this.deleteItem(),
      DownloadPDF: () => {
        // this.clickFood();
      },
      Home: () => void this.forceSaveThenClose(true),
      New: () => {
        this.clickNew();
        this.navigateHome();
      },
      Print: () => {
        //this.printOrExportNote("print");
      },
      CreateCopy: () => this.createCopyOfNote(),
      MoveToSpace: () => this.openMoveToSpaceDialog(),
      SaveAsTemplate: () => this.openSaveTemplateDialog(),
      SaveNote: () => this.saveNote(false),
      SaveNoteAndClose: () => this.saveNote(true),
      SaveNoteAndView: () => this.saveNoteAndView(),
      UserSettings: () => this.router.navigate(['user-settings']),
      FindSimilar: () => this.findSimilarNotes(),
      View: () => void this.forceSaveThenView(),
    };

    const action = actions[item];
    if (action) {
      action();
    } else {
      console.warn(`Unknown menu item: ${item}`);
    }
  }

  findSimilarNotes(): void {
    const noteId = this.note?._id;
    if (!noteId) {
      return;
    }
    this.similarNotesResults = [];
    this.similarNotesError = '';
    this.similarNotesLoading = true;
    this.similarNotesDialogVisible = true;

    this.data_service.findSimilarNotes(noteId).subscribe({
      next: (resp) => {
        this.similarNotesLoading = false;
        const raw: unknown = resp?.data;
        if (Array.isArray(raw)) {
          this.similarNotesResults = raw as SimilarNoteResult[];
        } else if (raw && typeof raw === 'object' && Array.isArray((raw as any)['results'])) {
          this.similarNotesResults = (raw as any)['results'] as SimilarNoteResult[];
        } else {
          this.similarNotesResults = [];
        }
      },
      error: (err) => {
        this.similarNotesLoading = false;
        this.similarNotesError =
          err?.error?.error || err?.error?.message || err?.message || 'Failed to find similar notes.';
      },
    });
  }


  getSimilarNoteId(note: SimilarNoteResult): string {
    return String(note._id ?? note.id ?? '');
  }

  formatSimilarNoteDate(note: SimilarNoteResult): string {
    const raw = note.updated_at ?? note.modified_at ?? note.modifiedDate ?? note.last_modified ?? '';
    if (!raw) {
      return '-';
    }
    try {
      return new Date(String(raw)).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return String(raw);
    }
  }

  openSaveTemplateDialog(): void {
    this.templateName = this.note?.Title ?? '';
    this.templateNameTouched = false;
    this.saveTemplateDialogVisible = true;
  }

  saveTemplateFromDialog(): void {
    this.templateNameTouched = true;

    const trimmedName = this.templateName.trim();
    if (!trimmedName || !this.note || this.isSavingTemplate) {
      return;
    }

    this.isSavingTemplate = true;
    this.applyChangesToNoteFromForm();

    this.data_service.createTemplateFromNote(this.note, trimmedName).subscribe(
      (resp) => {
        if (resp.status === 'success') {
          this.snackbarService.showSuccessMessage('Template saved successfully.');
          this.saveTemplateDialogVisible = false;
          // Refresh template cache for instant access
          this.data_service.refreshTemplateCache();
        } else {
          this.snackbarService.showErrorMessage('Failed to save template.');
        }
        this.isSavingTemplate = false;
      },
      (err) => {
        console.error('Error saving template:', err);
        this.snackbarService.showErrorMessage('Failed to save template.');
        this.isSavingTemplate = false;
      },
    );
  }

  toggleMobileMenu() {
    this.MobileMenuIsVisible = !this.MobileMenuIsVisible;
  }

  /**
   * Status picked in the dropdown. Keeps the visible Action required switch consistent with the
   * backend's rules — Action Required implies it, Inactive/Obsolete clear it — so the value the server
   * normalizes to is the one already shown. The status itself is never changed for the user.
   */
  onStatusChanged(event: any): void {
    if (this.isLoading() == true) return;
    const status = effectiveStatus(this.editNoteForm.controls['Status']?.value);
    const flag = this.editNoteForm.controls['actionRequired'];
    if (flag) {
      if (status === 'Action Required' && flag.value !== true) flag.setValue(true);
      else if ((status === 'Inactive' || status === 'Obsolete') && flag.value !== false) flag.setValue(false);
    }
    this.onOtherContentChanged(event);
  }

  onOtherContentChanged(event: any) {
    if (this.isLoading() == true) return;
    // Update note.Title to reflect changes in the title bar
    if (this.editNoteForm.controls['Title']) {
      this.note.Title = this.editNoteForm.controls['Title'].value;
    }
    this.markContentChanged();
  }

  onEditorDeltaContentUpdated(editor: string, event: any) {
    if (this.isLoading() == true) return;
    this.markContentChanged();
  }

  /** An image attachment was inserted into the Description/Comments editor (Insert Image dialog or
   * drag & drop): it belongs to the note like any other attachment. */
  onAttachmentImageInserted(payload: AttachmentImageInsertPayload): void {
    const attachmentId = payload?.attachmentId;
    if (!this.note || !attachmentId) return;
    const attachments = Array.isArray(this.note.Attachments) ? this.note.Attachments : [];
    if (!attachments.includes(attachmentId)) {
      this.note.Attachments = [...attachments, attachmentId];
    }
    if (!this.insertedImageAttachments.some((a) => a._id === attachmentId)) {
      this.insertedImageAttachments = [
        ...this.insertedImageAttachments,
        {
          _id: attachmentId,
          filename: payload.filename || attachmentId,
          mimeType: payload.mimeType || 'image/png',
          size: payload.fileSizeBytes ?? 0,
          isImage: true,
          userId: '',
          created_on: new Date().toISOString(),
        } as Attachment,
      ];
    }
  }

  /** An attachment was removed in the Attachments tab: its images in the note text go with it. */
  onAttachmentRemoved(attachmentId: string): void {
    if (!this.note || !attachmentId) return;
    const editorFields: Record<string, 'DescriptionHtml' | 'CommentsHtml'> = {
      EditDescription: 'DescriptionHtml',
      EditComments: 'CommentsHtml',
    };
    const handled = new Set<string>();
    // Mounted editors remove the nodes themselves (undoable; the form follows via contentUpdated).
    this.richEditors?.forEach((editor) => {
      const field = editorFields[editor.editorId];
      if (field && !editor.readOnly) {
        editor.removeAttachmentImages(attachmentId);
        handled.add(field);
      }
    });
    for (const field of ['DescriptionHtml', 'CommentsHtml'] as const) {
      if (handled.has(field)) continue;
      const control = this.editNoteForm?.get(field);
      const current = control?.value;
      if (typeof current === 'string' && current.includes(attachmentId)) {
        control!.patchValue(removeAttachmentImagesFromHtml(current, attachmentId), { emitEvent: false });
      }
    }
    this.insertedImageAttachments = this.insertedImageAttachments.filter((a) => a._id !== attachmentId);
    if (FileUploadService.isQueuedAttachmentId(attachmentId) && Array.isArray(this.note.Attachments)) {
      // Never reached the server, so no delete flush will drop it from Attachments.
      this.note.Attachments = this.note.Attachments.filter((id) => id !== attachmentId);
    }
    this.cdRef.markForCheck();
  }

  private applyAttachmentIdReplacement(oldId: string, newId: string): void {
    if (!this.note || !oldId || !newId) return;
    if (Array.isArray(this.note.Attachments) && this.note.Attachments.includes(oldId)) {
      this.note.Attachments = this.note.Attachments.map((id) => (id === oldId ? newId : id));
    }
    this.insertedImageAttachments = this.insertedImageAttachments.map((a) => (a._id === oldId ? { ...a, _id: newId } : a));
    for (const field of ['DescriptionHtml', 'CommentsHtml'] as const) {
      const control = this.editNoteForm?.get(field);
      const current = control?.value;
      if (typeof current === 'string' && current.includes(oldId)) {
        control!.patchValue(replaceAttachmentIdInHtml(current, oldId, newId), { emitEvent: false });
      }
      if (typeof this.note[field] === 'string' && this.note[field].includes(oldId)) {
        this.note[field] = replaceAttachmentIdInHtml(this.note[field], oldId, newId);
      }
    }
    this.cdRef.markForCheck();
  }

  onEditorHtmlContentUpdated(field: 'Description' | 'Comments', html: string) {
    // console.log('[Edit] onEditorHtmlContentUpdated called for', field, 'length:', html.length);
    if (this.isLoading()) return;
    //   console.log(`HTML content updated for ${field}:`, html);
    if (field === 'Description') {
      // console.log('[Edit] Patching DescriptionHtml form value');
      this.editNoteForm.patchValue({ DescriptionHtml: html }, { emitEvent: false });
    } else if (field === 'Comments') {
      this.editNoteForm.patchValue({ CommentsHtml: html }, { emitEvent: false });
    }

    this.markContentChanged();
  }


  setTitle(newTitle: string) {
    this.titleService.setTitle(newTitle);
  }

  updateStatusMessage() {
    this.statusMessage = '';
    if (this.isContentChanged) {
      this.statusMessage = 'Unsaved ';
    }
  }

  private markContentChanged(): void {
    this.setContentChanged(true);
    this.contentChangeCounter++;
    // Must run after the counter increments above — canDiscardSinceOpening
    // compares against it, and syncDiscardMenuItemState() inside
    // setContentChanged() would otherwise read the pre-increment value.
    this.syncDiscardMenuItemState();
    if (this.autosaveEnabled) {
      this.autosaveState = 'unsaved';
      this.scheduleAutosave();
    }
  }

  private setContentChanged(isContentChanged: boolean): void {
    this.isContentChanged = isContentChanged;
    this.unsavedChangesState.setHasUnsavedChanges(isContentChanged);
    this.updateStatusMessage();
  }

  // ##############################################################################
  // AUTOSAVE (Stage 1 — local-first, debounced, cancel-safe)
  // ##############################################################################

  /** Resets the 5s idle debounce; starts (but does not reset) the 25s max-interval timer. */
  private scheduleAutosave(): void {
    if (!this.autosaveEnabled) return;

    if (this.autosaveDebounceTimer) {
      clearTimeout(this.autosaveDebounceTimer);
    }
    this.autosaveDebounceTimer = setTimeout(() => {
      this.autosaveDebounceTimer = null;
      void this.performAutosave('debounce');
    }, NoteEditBase.AUTOSAVE_DEBOUNCE_MS);

    if (!this.autosaveMaxTimer) {
      this.autosaveMaxTimer = setTimeout(() => {
        this.autosaveMaxTimer = null;
        void this.performAutosave('max-interval');
      }, NoteEditBase.AUTOSAVE_MAX_INTERVAL_MS);
    }
  }

  /**
   * Local-first autosave: builds the note from the current form/editor state
   * and writes it straight to IndexedDB (marking it dirty for the existing
   * background sync cycle), skipping validation, attachment upload, dialogs
   * and any remote round-trip — those stay on the explicit Save path and on
   * the "leaving the editor" force-saves (View/Close), which flush attachments
   * via forceAutosaveIfDirty() before calling this.
   *
   * Serialized: only one autosave runs at a time. If new edits arrive while
   * one is in flight, another run is queued immediately after it finishes.
   *
   * `notify` surfaces user-visible feedback (a success toast, or an error
   * dialog on failure) — used only for the explicit Ctrl+S/Cmd+S shortcut, so
   * regular debounce/max-interval/backgrounding autosaves stay silent.
   */
  private async performAutosave(reason: string, notify: boolean = false): Promise<void> {
    if (this.autosaveDebounceTimer) {
      clearTimeout(this.autosaveDebounceTimer);
      this.autosaveDebounceTimer = null;
    }
    if (this.autosaveMaxTimer) {
      clearTimeout(this.autosaveMaxTimer);
      this.autosaveMaxTimer = null;
    }

    if (!this.autosaveEnabled || !this.note || !this.editNoteForm) return;

    if (this.autosaveInFlight) {
      this.autosavePendingRerun = true;
      if (notify) {
        this.alertService.success('Changes saved successfully!', { life: 4000 });
      }
      return;
    }

    if (this.contentChangeCounter === this.autosaveLastSavedGeneration) {
      // Nothing new since the last local save — the note is already saved,
      // so a manual Ctrl+S still gets its confirmation.
      if (notify) {
        this.alertService.success('Changes saved successfully!', { life: 4000 });
      }
      return;
    }

    this.autosaveInFlight = true;
    this.autosaveState = 'saving-local';
    const generation = this.contentChangeCounter;
    this.applyChangesToNoteFromForm();
    if (this.isSpaceEditable) {
      this.note.localDraft = true;
    } else {
      delete this.note.localDraft;
    }

    try {
      await this.data_service.autosaveNote(this.note);
      this.temporaryStatus = false;
      this.autosaveLastSavedGeneration = generation;
      void this.touchOpeningSnapshotActivity();

      // Only clear the dirty flag if nothing changed while we were saving —
      // otherwise a newer edit would be silently marked "saved".
      if (this.contentChangeCounter === generation) {
        this.setContentChanged(false);
      }

      // Sync still pending unless already synced; "Syncing…" is shown once a cycle is actually running
      // (watchForSyncCompletion follows syncing$), and "Offline" is derived from connectivity.
      this.autosaveState = this.note.syncStatus === 'synced' ? 'saved' : 'saved-local';
      if (this.note.localDraft) {
        // Nothing will sync until the first explicit save (lockSpaceField) — don't wait on a cycle.
        if (notify) {
          this.alertService.success('Changes saved successfully!', { life: 4000 });
        }
        return;
      }
      this.watchForSyncCompletion();

      // The periodic sync timer already gets reset to its 30s floor whenever a
      // note is marked dirty (SyncCoordinator listens for that itself), but
      // that still means waiting up to 30s for the next tick. Nudge an
      // incremental sync now so autosave's "syncing" state resolves in
      // roughly a second instead — this reuses the exact same push/pull
      // cycle the periodic timer would have run, it's just triggered early;
      // forceIncrementalSync() already de-dupes against a cycle already in
      // flight, so this can't race the periodic timer or pile up calls.
      if (this.isOnline) {
        void this.syncCoordinator.forceIncrementalSync('auto');
      }

      if (notify) {
        this.alertService.success('Changes saved successfully!', { life: 4000 });
      }
    } catch (err: any) {
      console.error(`[Autosave] local save failed (reason=${reason})`, err);
      // Local persistence failed: the edits are still only in the editor (isContentChanged stays true),
      // so the unsaved-changes guards keep protecting them. The next edit or Ctrl+S retries.
      this.autosaveState = 'save-failed';
      if (notify) {
        const msg = err?.message || 'Error saving note.';
        this.gDS.showGlobalDialog('Save Failed', 'Could not save note', msg, DialogType.Error);
      }
    } finally {
      this.autosaveInFlight = false;
      if (this.autosavePendingRerun) {
        this.autosavePendingRerun = false;
        void this.performAutosave('rerun-after-inflight');
      } else if (this.contentChangeCounter !== generation) {
        this.scheduleAutosave();
      }
    }
  }

  /** Immediately flushes any pending debounced edit — used for Ctrl+S, View, Close, and backgrounding.
   * Also uploads any pending attachment additions/renames/deletions first: the debounced/max-interval
   * autosave ticks deliberately skip attachment upload (see performAutosave()), so this is the only
   * place that does it in autosave mode — without it, attachments added via drag-and-drop would never
   * reach the server since the explicit Save buttons are hidden while autosave is enabled. */
  private async forceAutosaveIfDirty(reason: string): Promise<void> {
    if (!this.autosaveEnabled) return;

    const hasPendingAttachments = this.hasPendingAttachmentChanges();
    console.log(`[Attachments] forced save check (trigger=${reason})`, {
      uploaderMounted: !!this.fileUploadComponent,
      hasPendingAttachments,
      isContentChanged: this.isContentChanged,
    });
    if (!this.isContentChanged && !hasPendingAttachments) return;

    if (hasPendingAttachments) {
      const uploadError = await this.flushPendingAttachments(`forced-save:${reason}`);
      if (uploadError) {
        this.gDS.showGlobalDialog('Save Failed', 'Could not save attachments', uploadError, DialogType.Error);
      } else {
        // Attachments changed but form fields may not have — force performAutosave()
        // past its "nothing new since last save" guard so the updated Attachments
        // array actually gets persisted to IndexedDB and queued for sync.
        this.markContentChanged();
      }
    }

    await this.performAutosave(reason);
  }

  /**
   * While a note is saved locally but not yet synced, follow the existing SyncCoordinator:
   * syncing$ drives "Syncing…", and when a cycle ends (its falling edge, which also fires for
   * failed cycles that never emit onSyncCompleted) the note's syncStatus in IndexedDB decides
   * between "Saved & synced" and "Sync failed".
   */
  private watchForSyncCompletion(): void {
    if (this.autosaveSyncSub) return;

    const sub = new Subscription();
    this.autosaveSyncSub = sub;

    sub.add(
      this.syncCoordinator.syncing$.pipe(takeUntil(this.destroy$)).subscribe((running) => {
        this.syncRunning = running;
        if (!this.isAwaitingSync()) return;
        if (running) {
          this.autosaveState = 'syncing';
          this.cdRef.markForCheck();
        } else {
          void this.reconcileSyncState();
        }
      }),
    );
    sub.add(
      this.syncCoordinator
        .onSyncCompleted()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => void this.reconcileSyncState()),
    );
  }

  /** Saved locally, no newer edits in the editor, sync not yet confirmed. */
  private isAwaitingSync(): boolean {
    return (
      !this.isContentChanged &&
      (this.autosaveState === 'saved-local' || this.autosaveState === 'syncing' || this.autosaveState === 'sync-failed')
    );
  }

  private async reconcileSyncState(): Promise<void> {
    const noteId = this.note?._id;
    if (!noteId || !this.isAwaitingSync()) return;

    const result = await firstValueFrom(this.data_service.getNote(noteId));
    // Re-check: the user may have edited while the lookup was in flight.
    if (!this.isAwaitingSync()) return;
    const freshStatus = result.status === 'success' ? result.data?.syncStatus : undefined;

    if (freshStatus === 'synced') {
      // SyncCoordinator pushes/refreshes IndexedDB from its own note
      // reference, not this.note, so the _rev it got back from the
      // server never otherwise reaches the object autosave keeps
      // mutating. Without this, the next autosave writes this.note's
      // stale _rev straight back into IndexedDB, and the following push
      // hits a 409 (harmless — the remote client already retries with
      // the latest rev — but it's a wasted round-trip and console noise
      // on every single edit after the first).
      if (this.note && result.data) {
        if (result.data._rev) this.note._rev = result.data._rev;
        if ((result.data as any).remote_rev) (this.note as any).remote_rev = (result.data as any).remote_rev;
      }
      this.autosaveState = 'saved';
      this.autosaveSyncSub?.unsubscribe();
      this.autosaveSyncSub = undefined;
    } else if (!this.syncRunning) {
      // A cycle ended and the note is still dirty. Offline vs failed is decided in
      // autosaveStatusView from connectivity; here we just record that the attempt did not succeed.
      this.autosaveState = 'sync-failed';
    }
    this.cdRef.markForCheck();
  }

  /** Mirrors SyncCoordinator.syncing$ (a fresh cycle may already have started after the one that just ended). */
  private syncRunning = false;

  /**
   * Icon/label/animation for the footer save/sync status indicator. Derived from the existing
   * autosave state + dirty flag + connectivity, so it can never claim "synced" while the editor
   * holds unsaved edits, and a sync problem never reads as a failed local save.
   */
  get autosaveStatusView(): { icon: string; label: string; colorClass: string; spin: boolean } {
    let state = this.autosaveState;
    if (this.autosaveInFlight) {
      state = 'saving-local';
    } else if (this.isContentChanged && state !== 'save-failed') {
      state = 'unsaved';
    }

    const awaitingSync = state === 'saved-local' || state === 'syncing' || state === 'sync-failed';
    if (awaitingSync && !this.connectivity.fullyOnline) {
      return { icon: 'cloud_off', label: 'Saved locally · Offline', colorClass: 'autosave-status--offline', spin: false };
    }

    switch (state) {
      case 'unsaved':
        return { icon: 'edit', label: 'Unsaved changes', colorClass: 'autosave-status--unsaved', spin: false };
      case 'saving-local':
        return { icon: 'save', label: 'Saving…', colorClass: 'autosave-status--saving', spin: false };
      case 'saved-local':
        return { icon: 'cloud_queue', label: 'Saved locally · Sync pending', colorClass: 'autosave-status--queued', spin: false };
      case 'syncing':
        return { icon: 'sync', label: 'Syncing…', colorClass: 'autosave-status--syncing', spin: true };
      case 'sync-failed':
        return { icon: 'sync_problem', label: 'Saved locally · Sync failed', colorClass: 'autosave-status--failed', spin: false };
      case 'save-failed':
        return { icon: 'error', label: 'Save failed', colorClass: 'autosave-status--failed', spin: false };
      case 'saved':
        return { icon: 'cloud_done', label: 'Saved & synced', colorClass: 'autosave-status--saved', spin: false };
      case 'clean':
      default:
        return { icon: 'cloud_done', label: 'No changes · Synced', colorClass: 'autosave-status--clean', spin: false };
    }
  }

  @HostListener('document:visibilitychange')
  onVisibilityChangeAutosave(): void {
    if (document.visibilityState === 'hidden') {
      void this.forceAutosaveIfDirty('visibilitychange');
    }
  }

  @HostListener('window:pagehide', ['$event'])
  onPageHideAutosave(event?: PageTransitionEvent): void {
    void this.forceAutosaveIfDirty('pagehide');
    // Window closing / reloading: the session ends normally (its edits were just saved). A page kept
    // in the back-forward cache (persisted) may come back, so its session stays.
    if (!event?.persisted && this.openingSnapshot) {
      // The IndexedDB delete may not complete while the window is closing, so also record the ended
      // session synchronously; the next startup check then removes the snapshot.
      this.editSessionService.markSessionEnded(this.openingSnapshot.sessionId);
      void this.deleteOpeningSnapshotIfAny();
    }
  }

  /** Force-save (if autosave is on) then navigate home. No-op save when autosave is off — identical to prior behavior.
   * This is the Close SplitButton's main action: force-save locally, queue
   * synchronization (both already covered by forceAutosaveIfDirty), delete
   * the session snapshot, and close — the record lock itself is released
   * separately in ngOnDestroy()/route-change teardown, same as before. */
  private async forceSaveThenClose(clearHistory: boolean): Promise<void> {
    if (this.isClosing) return;
    // Set synchronously so the very next change-detection pass shows "Closing…" (instant click feedback).
    this.isClosing = true;
    this.cdRef.markForCheck();
    try {
      // Only the local IndexedDB write has to finish before leaving; everything else can follow.
      await this.forceAutosaveIfDirty('close');
    } catch (err) {
      console.error('[Close] save before close failed', err);
    }
    await this.lockSpaceField();
    // Housekeeping only (deletes the session snapshot; errors are handled inside) — never block navigation on it.
    void this.deleteOpeningSnapshotIfAny();
    if (clearHistory) {
      this.navigationService.clearHistory();
    }
    this.navigateHome();
  }

  /** Extra navigation state when switching from Edit to View of this note (the page subclass adds
   * the open tab, so View opens on the same one). */
  protected modeSwitchState(): Record<string, unknown> {
    return {};
  }

  /** Force-save (if autosave is on) then navigate to the View page. No-op save when autosave is off — identical to prior behavior. */
  private async forceSaveThenView(): Promise<void> {
    await this.forceAutosaveIfDirty('view');
    await this.lockSpaceField();
    this.router.navigate([this.viewRoute, this.note?._id], {
      state: {
        ...this.modeSwitchState(),
        filter: this.filter,
        homeState: this.homeState,
      },
    });
  }

  // ##############################################################################
  // AUTOSAVE (Stage 2 — opening snapshot & discard)
  // ##############################################################################

  /** Snapshots this note's editable content as it stands right when this
   * edit session opens (before any edits are possible), so "Discard changes
   * since opening…" can restore it later. IndexedDB-only; never synced to
   * the backend, and doesn't duplicate attachment binaries. */
  private async createOpeningSnapshot(): Promise<void> {
    if (!this.note?._id) return;
    const sessionRecord = this.editSessionService.getSessionRecord(this.note._id);
    if (!sessionRecord) return;

    const nowIso = new Date().toISOString();
    const snapshot: NoteEditSessionSnapshot = {
      noteId: this.note._id,
      sessionId: sessionRecord.sessionId,
      openedAt: sessionRecord.openedAt,
      lastActiveAt: nowIso,
      openingRev: this.note._rev,
      instanceId: this.editSessionService.getTabId(),
      status: 'active',
      lockRef: sessionRecord.sessionId,
      content: this.currentSnapshotContent(),
    };

    try {
      await this.data_service.saveEditSessionSnapshot(snapshot);
      if (this.editSessionEnded || this.note?._id !== snapshot.noteId) {
        // The session ended (or moved to another note) while this was being written.
        await this.data_service.deleteEditSessionSnapshot(snapshot.noteId, snapshot.sessionId);
        return;
      }
      this.openingSnapshot = snapshot;
    } catch (err) {
      console.error('[Autosave] Failed to create opening snapshot', err);
    } finally {
      this.syncDiscardMenuItemState();
    }
  }

  /** Refreshes the opening snapshot's lastActiveAt on every successful
   * autosave, so Stage 3's startup abandonment check (which classifies a
   * session as abandoned once lastActiveAt is old enough) never mistakes an
   * ordinary reload mid-way through a long, still-actively-autosaving
   * session for an abandoned one. */
  private async touchOpeningSnapshotActivity(): Promise<void> {
    if (!this.openingSnapshot || !this.note?._id) return;
    this.openingSnapshot.lastActiveAt = new Date().toISOString();
    try {
      await this.data_service.saveEditSessionSnapshot(this.openingSnapshot);
    } catch (err) {
      console.error('[Autosave] Failed to refresh opening snapshot activity', err);
    }
  }

  /** The editable-content subset of this.note, in the same shape stored in
   * (and restored from) an opening snapshot. */
  private currentSnapshotContent(): NoteEditSessionSnapshotContent {
    return extractSnapshotContent(this.note);
  }

  /** True once anything has changed since the opening snapshot was taken.
   * Reuses the same monotonic edit-generation counter Stage 1 already uses
   * for stale-save protection instead of deep-diffing content on every
   * check — cheap, and exactly tracks "has markContentChanged() fired
   * since the snapshot was captured." */
  get canDiscardSinceOpening(): boolean {
    return (
      this.autosaveEnabled &&
      !!this.openingSnapshot &&
      this.contentChangeCounter !== this.openingSnapshotBaselineCounter
    );
  }

  /** Opens the "Discard changes since opening…" confirmation. `viaClose`
   * distinguishes the Close-SplitButton dropdown from the page-menu item,
   * since "Keep changes"/"Discard changes" continue editing or close
   * afterward depending on which one triggered the dialog. */
  openDiscardChangesDialog(viaClose: boolean): void {
    if (!this.canDiscardSinceOpening) return;
    this.discardInvokedViaClose = viaClose;
    this.confirmationService.confirm({
      header: 'Discard editing changes?',
      message:
        'This restores the note to how it was when you opened the editor. Changes made during this editing session will be removed.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Discard changes',
      rejectLabel: 'Keep changes',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => void this.onDiscardChangesConfirmed(),
      reject: () => void this.onKeepChangesFromDiscardDialog(),
    });
  }

  private async onKeepChangesFromDiscardDialog(): Promise<void> {
    const viaClose = this.discardInvokedViaClose;
    this.discardInvokedViaClose = false;
    if (viaClose) {
      await this.forceSaveThenClose(true);
    } else {
      await this.forceAutosaveIfDirty('keep-changes-dialog');
    }
  }

  private async onDiscardChangesConfirmed(): Promise<void> {
    const viaClose = this.discardInvokedViaClose;
    this.discardInvokedViaClose = false;
    await this.restoreOpeningSnapshot();
    if (viaClose) {
      await this.forceSaveThenClose(true);
    }
  }

  /** Restores this.note, the reactive form, and tag/related-note UI state to
   * the opening snapshot's content, then immediately persists that as a new
   * dirty update. Per spec this must NOT roll back synchronization or
   * version history — it creates a new restoring update on top, same as any
   * other autosave. */
  private async restoreOpeningSnapshot(): Promise<void> {
    if (!this.openingSnapshot || !this.note || !this.editNoteForm) return;
    const c = this.openingSnapshot.content;

    this.note.Title = c.Title;
    this.note.DescriptionHtml = c.DescriptionHtml;
    this.note.DescriptionDelta = c.DescriptionDelta;
    this.note.CommentsHtml = c.CommentsHtml;
    this.note.CommentsDelta = c.CommentsDelta;
    this.note.Keywords = c.Keywords;
    (this.note as any).AiKeywords = c.AiKeywords;
    this.note.Status = c.Status;
    this.note.actionRequired = c.actionRequired;
    this.note.priority = c.priority;
    this.note.dueDate = c.dueDate;
    this.note.completedOn = c.completedOn;
    this.note.NoteType = c.NoteType;
    this.note.Confidential = c.Confidential;
    this.note.ReferenceDate = c.ReferenceDate;
    this.note.StartDate = c.StartDate;
    this.note.EndDate = c.EndDate;
    this.note.Tags = [...c.Tags];
    this.note.RelatedNotes = [...c.RelatedNotes];
    if (c.spaceId) {
      this.note.spaceId = c.spaceId;
    }

    this.statusOptions = statusOptionsFor(c.Status);
    this.noteTags = this.resolveSpaceNoteTags(this.note);
    this.relatedNotes = getOrderedRelatedNoteIds(c.RelatedNotes);
    this.refreshRelatedNoteObjects();

    this.editNoteForm.patchValue({
      Title: c.Title,
      DescriptionHtml: c.DescriptionHtml,
      DescriptionDelta: c.DescriptionDelta,
      CommentsHtml: c.CommentsHtml,
      CommentsDelta: c.CommentsDelta,
      Keywords: c.Keywords,
      AiKeywords: c.AiKeywords,
      Status: c.Status,
      actionRequired: c.actionRequired === true,
      priority: normalizePriority(c.priority),
      dueDate: toOptionalDate(c.dueDate),
      completedOn: toOptionalDate(c.completedOn),
      NoteType: c.NoteType,
      Confidential: c.Confidential,
      ReferenceDate: c.ReferenceDate,
      StartDate: c.StartDate,
      EndDate: c.EndDate,
      spaceId: c.spaceId ?? this.note.spaceId,
    });
    this.cdRef.detectChanges();

    this.markContentChanged();
    this.openingSnapshotBaselineCounter = this.contentChangeCounter;
    this.syncDiscardMenuItemState();
    await this.performAutosave('discard-restore');
  }

  private async deleteOpeningSnapshotIfAny(): Promise<void> {
    if (!this.openingSnapshot) return;
    const { noteId, sessionId } = this.openingSnapshot;
    this.openingSnapshot = null;
    try {
      await this.data_service.deleteEditSessionSnapshot(noteId, sessionId);
    } catch (err) {
      console.error('[Autosave] Failed to delete opening snapshot', err);
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.isContentChanged && !this.hasPendingAttachmentChanges()) {
      return;
    }

    event.preventDefault();
    event.returnValue = true;
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    // Staged attachment changes that failed to send (or were staged offline) count as unsaved too —
    // an autosave after a failed flush clears isContentChanged, but the attachment change is still pending.
    if (this.isContentChanged || this.hasPendingAttachmentChanges()) {
      return new Promise<boolean>((resolve) => {
        this.deactivateResolver = resolve;
        this.unsavedChangesDialogVisible = true;
      });
    }
    return true;
  }

  onConfirmDiscard(): void {
    this.unsavedChangesDialogVisible = false;
    if (this.pendingCancelEdits) {
      this.pendingCancelEdits = false;
      this.completeCancelEdits();
    } else if (this.deactivateResolver) {
      this.deactivateResolver(true);
      this.deactivateResolver = null;
    }
  }

  onCancelDiscard(): void {
    this.unsavedChangesDialogVisible = false;
    if (this.deactivateResolver) {
      this.deactivateResolver(false);
      this.deactivateResolver = null;
    }
  }

  onClose() {
    void this.forceSaveThenClose(false);
  }

  private navigateHome(refresh = false): void {
    RichEditorComponent.clearPersistedCollapsibleStateForNote(this.note?._id ?? this.noteId);

    const state: any = {
      filter: this.filter,
      homeState: this.homeState,
    };
    if (refresh) {
      state.refresh = true;
    }
    void this.router.navigate(['/home'], { state }).then((navigated) => {
      // Navigation refused (e.g. the user chose "Keep Editing" in the unsaved-changes dialog): re-enable Close.
      if (!navigated) {
        this.isClosing = false;
        this.cdRef.markForCheck();
      }
    });
  }

  toggleHeaderFields(): void {
    console.log('toggle header fields now =', !this.isHeaderVisible);
    this.isHeaderVisible = !this.isHeaderVisible;
  }

  toggleDescription(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  toggleAllCollapsibleSections(): void {
    if (!this.descriptionEditor?.editor) {
      console.warn('Description editor not available');
      return;
    }

    const newState = !this.areAllCollapsibleExpanded;
    this.areAllCollapsibleExpanded = newState;

    // Call the editor's method to toggle all sections
    this.descriptionEditor.toggleAllCollapsibleSections(newState);
  }

  toggleFileAttachments(): void {
    this.isFileAttachmentsExpanded = !this.isFileAttachmentsExpanded;
  }

  toggleTags(): void {
    this.isTagsExpanded = !this.isTagsExpanded;
  }

  openTagManager(): void {
    this.router.navigate(['/edit-tags']);
  }

  toggleKeywords(): void {
    this.isKeywordsExpanded = !this.isKeywordsExpanded;
  }

  toggleComments(): void {
    this.isCommentsExpanded = !this.isCommentsExpanded;
  }

  toggleFavorite(): void {
    const noteId = this.note?._id;
    if (!noteId) {
      this.snackbarService.showErrorMessage('No note selected.');
      return;
    }
    const isCurrentlyFavorite = this.favoriteNoteIds.includes(noteId);
    const updated = isCurrentlyFavorite
      ? this.favoriteNoteIds.filter((id) => id !== noteId)
      : [...this.favoriteNoteIds, noteId];

    // Offline-first: the local flip is the source of truth. userSettings.updateFavorites()
    // already writes it to IndexedDB (and skips the network call entirely when offline), so a
    // failed/unreachable remote push below must NOT undo this — it just means the push will be
    // retried on a future sync rather than the favorite silently reverting under the user.
    this.favoriteNoteIds = updated;
    this.isFavorite = !isCurrentlyFavorite;
    this.userSettings.setLocal({ favoriteNoteIds: updated });

    this.userSettings.updateFavorites(updated).subscribe({
      next: () => {
        const message = isCurrentlyFavorite ? 'Removed from favorites' : 'Added to favorites';
        this.snackbarService.showSuccessMessage(message);
      },
      error: (err) => {
        console.warn('Failed to sync favorite status to server (kept local change):', err);
      },
    });
  }

  private updateFavoriteState(): void {
    const noteId = this.note?._id;
    if (!noteId) {
      this.isFavorite = false;
      return;
    }
    this.isFavorite = this.favoriteNoteIds.includes(noteId);
  }

  private normalizeFavoriteNoteIds(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((id) => typeof id === 'string');
  }

  clickNew(): void {
    console.log('clickNew');
    this.createNewNote();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    const isModKeyPressed = event.ctrlKey || event.metaKey;
    const pressedKey = (event.key || '').toLowerCase();
    const isSaveCombo = isModKeyPressed && (pressedKey === 's' || event.code === 'KeyS');

    if (isSaveCombo) {
      event.preventDefault();
      void this.handleSaveShortcut();
    }
  }

  /** Called after the user's first explicit save of a new note: makes the Space field read-only for
   * the rest of this edit session and, if autosave has been keeping the note a local-only draft,
   * rewrites it without that flag and nudges a sync so it's uploaded into the chosen Space. */
  private async lockSpaceField(): Promise<void> {
    if (!this.isSpaceEditable) return;
    this.isSpaceEditable = false;
    this.editNoteForm?.get('spaceId')?.disable({ emitEvent: false });
    this.cdRef.markForCheck();

    if (!this.note?.localDraft) return;
    delete this.note.localDraft;
    try {
      await this.data_service.autosaveNote(this.note);
    } catch (err) {
      // Still a draft in IndexedDB: keep the Space editable so the next explicit save retries this.
      console.error('[Space] could not release the local draft', err);
      this.note.localDraft = true;
      this.isSpaceEditable = true;
      this.editNoteForm?.get('spaceId')?.enable({ emitEvent: false });
      return;
    }
    this.watchForSyncCompletion();
    if (this.isOnline) {
      void this.syncCoordinator.forceIncrementalSync('auto');
    }
  }

  async handleSaveShortcut(): Promise<void> {
    if (this.isSaving) {
      return;
    }
    if (this.autosaveEnabled) {
      // One Ctrl+S in the editor reaches here twice (the editor's own shortcut and the window
      // keydown listener): the second is ignored while the first is still saving.
      if (this.saveShortcutInFlight) return;
      this.saveShortcutInFlight = true;
      try {
        await this.forcedShortcutAutosave();
      } finally {
        this.saveShortcutInFlight = false;
      }
      return;
    }
    await this.saveNote(false);
  }

  private saveShortcutInFlight = false;

  /** Ctrl+S with autosave on. */
  private async forcedShortcutAutosave(): Promise<void> {
    // Ctrl+S in autosave mode is an immediate forced *local* save of the note's own
    // fields, not the full validated Save — but it must still flush any pending
    // attachment rename/delete/upload first (same as View/Close's
    // forceAutosaveIfDirty()): with the Save button removed from the editor, Ctrl+S is
    // now one of only three ways a user can persist those changes at all, so it can no
    // longer silently skip them the way the debounce/max-interval/backgrounding
    // autosaves still safely can (those never claim to have saved attachment edits).
    console.log('[Attachments] forced save check (trigger=ctrl-s)', {
      uploaderMounted: !!this.fileUploadComponent,
      hasPendingAttachments: this.hasPendingAttachmentChanges(),
      isContentChanged: this.isContentChanged,
    });
    if (this.hasPendingAttachmentChanges()) {
      const uploadError = await this.flushPendingAttachments('ctrl-s');
      if (uploadError) {
        this.gDS.showGlobalDialog('Save Failed', 'Could not save attachments', uploadError, DialogType.Error);
        return;
      }
      this.markContentChanged();
    }
    await this.performAutosave('shortcut-forced', true);
    if (this.autosaveState !== 'save-failed') {
      await this.lockSpaceField();
    }
    // Autosave never refuses to save (nothing typed may be lost), so an explicit Ctrl+S points out
    // invalid tasks instead of blocking.
    this.warnAboutInlineTaskIssues();
  }

  /** Outlines invalid inline tasks and says what's wrong; false when there was something to fix. */
  private warnAboutInlineTaskIssues(): boolean {
    const issues = this.descriptionEditor?.validateInlineTasks() ?? [];
    if (!issues.length) return true;
    const more = issues.length > 1 ? ` (${issues.length - 1} more task problem${issues.length > 2 ? 's' : ''})` : '';
    this.snackbarService.showWarningMessage(`Saved, but a task needs attention: ${issues[0].message}${more}`);
    return false;
  }

  clearTitle() {
    //const titleField = this.TitleField.nativeElement;
    this.note!.Title = '';
    //titleField.focus();
  }

  // ##############################################################################
  // SAVING & DELETING DATA
  // ##############################################################################
  createNewNote(): void {
    console.log('🟢 In createNewNote()');

    this.data_service.createNote().subscribe(
      (response) => {
        if (response.status === 'success' && response.data) {
          const newNote = response.data;
          console.log('✅ New temporary note created:', newNote._id);

          this.router.navigate([this.editRoute, newNote._id], {
            state: {
              filter: this.filter,
              homeState: this.homeState,
            },
          });

          this.alertService.success('New note created successfully.', {
            keepAfterRouteChange: false,
            life: 3000,
          });
        } else {
          console.error('⚠️ Failed to create note');
          this.alertService.warn('Failed to create note.', {
            keepAfterRouteChange: false,
            life: 4000,
          });
        }
      },
      (err) => {
        console.error('❌ Unexpected error in createNewNote():', err);
        this.alertService.error(`An unexpected error occurred while creating the note.`, {
          keepAfterRouteChange: false,
          life: 5000,
        });
      },
    );
  }

  createCopyOfNote(): void {
    if (!this.note) {
      this.snackbarService.showErrorMessage('Note is not available to copy.');
      return;
    }

    this.loadingCounter++;

    this.data_service.createNoteCopy(this.note).subscribe({
      next: (response: any) => {
        if (response.status === 'success' && response.data) {
          const newNote = response.data;
          console.log('✅ Copied note created locally:', newNote._id);

          this.router.navigate([this.editRoute, newNote._id], {
            state: {
              filter: this.filter,
              homeState: this.homeState,
            },
          });
        } else {
          const msg = response.error?.message || 'Failed to create note copy.';
          console.warn('⚠️ createCopyOfNote():', msg);
          this.alertService?.warn?.(msg, { life: 4000 });
        }

        this.loadingCounter--;
      },

      error: (err) => {
        console.error('❌ createCopyOfNote(): unexpected error:', err);
        this.alertService?.error?.('An unexpected error occurred while creating a note copy.', { life: 5000 });
        this.loadingCounter--;
      },
    });
  }

  //   createNewNote(): void {
  //     console.log('in createNewNote()');
  //     this.data_service.new_note().subscribe(
  //       (response: any) => {
  //         const newNoteResponse = response as NewNoteResponse;
  //         console.log('Status: ', newNoteResponse);
  //         this.router.navigate(['/edit', newNoteResponse.id], {
  //           state: { filter: this.filter },
  //         });
  //       },
  //       (error) => {
  //         console.error('Error fetching new note:', error);
  //         // Handle any errors here
  //       },
  //     );
  //   }

  applyChangesToNoteFromForm(): void {
    if (!this.note || !this.editNoteForm) return;
    const v = this.editNoteForm.value;
    // Images whose attachment the text showed until now: if one is gone, its file is deleted on the
    // next save (see deleteRemovedImageAttachments), unless something still refers to it by then.
    const previousImageIds = [...extractAttachmentImageIds(this.note.DescriptionHtml), ...extractAttachmentImageIds(this.note.CommentsHtml)];
    for (const id of previousImageIds) {
      if (!isAttachmentReferencedInHtml(id, v.DescriptionHtml, v.CommentsHtml)) {
        this.removedImageAttachmentIds.add(id);
      }
    }

    this.note.Title = v.Title;
    this.note.DescriptionDelta = v.DescriptionDelta;
    this.note.DescriptionHtml = v.DescriptionHtml;
    this.note.Keywords = v.Keywords;
    (this.note as any).AiKeywords = v.AiKeywords;
    this.note.Tags = this.noteTags.map((tag) => tag._id);
    this.note.RelatedNotes = toRelatedNotesPayload(this.relatedNotes);
    this.note.CommentsDelta = v.CommentsDelta;
    this.note.CommentsHtml = v.CommentsHtml;
    // Every image shown in the text is an attachment of the note (also repairs notes whose images were
    // uploaded before inserted images were recorded in Attachments).
    const inlineImageIds = [...extractAttachmentImageIds(v.DescriptionHtml), ...extractAttachmentImageIds(v.CommentsHtml)];
    const knownAttachments = Array.isArray(this.note.Attachments) ? this.note.Attachments : [];
    const missingImageIds = inlineImageIds.filter((id, i) => !knownAttachments.includes(id) && inlineImageIds.indexOf(id) === i);
    if (missingImageIds.length) {
      this.note.Attachments = [...knownAttachments, ...missingImageIds];
    }
    this.note.ReferenceDate = v.ReferenceDate ? new Date(v.ReferenceDate).toISOString() : this.note.created_on;
    this.note.StartDate = v.StartDate ? new Date(v.StartDate).toISOString() : undefined;
    this.note.EndDate = v.EndDate ? new Date(v.EndDate).toISOString() : undefined;
    this.note.Confidential = !!v.Confidential;
    this.note.Status = v.Status;
    this.note.actionRequired = v.actionRequired === true;
    this.note.priority = normalizePriority(v.priority);
    this.note.dueDate = toOptionalIsoDate(v.dueDate);
    this.note.completedOn = toOptionalIsoDate(v.completedOn);
    // Inline tasks live in the description; keep fields only the server knows (e.g. comments).
    const tasks = mergeInlineTasks(extractInlineTasksFromHtml(v.DescriptionHtml), this.note.tasks);
    if (tasks.length || this.note.tasks !== undefined) {
      this.note.tasks = tasks;
    }
    if (this.isSpaceEditable && v.spaceId) {
      this.note.spaceId = v.spaceId;
    }
    const noteType = v.NoteType ?? this.note.NoteType ?? 'To Do';
    this.note.NoteType = typeof noteType === 'string' ? noteType.trim() : noteType;
    if (this.note.type !== 'note' && this.note.type !== 'template') {
      this.note.type = 'note';
    }
    if (!this.note.Project || this.note.Project === 0) {
      this.note.Project = 16;
      this.note.ProjectName = 'Obsolete_Field';
    }

    // ... your existing project lookup, etc.
  }

  async addQuickTag(): Promise<void> {
    if (typeof this.quickTagName !== 'string') {
      const tag = this.quickTagName;
      this.resetQuickTagInput();
      await this.attachTag(tag);
      return;
    }

    const value = this.quickTagInputText.trim();
    if (!value) {
      return;
    }

    // Names are matched within the scope the user is adding to only: "Architecture" can exist both as a
    // Space tag and as a private one, and they're different tags (never merged by name).
    const scope = this.effectiveNewTagScope;
    const existingCatalogTag = this.findTagByName(value, scope);
    if (existingCatalogTag) {
      this.resetQuickTagInput();
      if (!(await this.attachTag(existingCatalogTag)) && this.isTagOnNote(existingCatalogTag)) {
        this.alertService.warn(`Tag "${value}" is already on this note.`);
      }
      return;
    }

    if (scope === TAG_SCOPE.PRIVATE && !this.canChangePrivateTags) {
      this.alertService.warn(this.privateTagsBlockedReason);
      return;
    }

    const payload = this.buildTagPayload({
      name: value,
      tagType: this.defaultTagType,
      slug: this.slugify(value),
      color: '#2563EB',
      icon: 'tag',
      sortOrder: this.getNextTagSortOrder(),
      isActive: true,
    });
    if (scope === TAG_SCOPE.SPACE) {
      payload.spaceId = this.tagSpaceId ?? undefined;
    }

    this.isTagMutating = true;
    try {
      const resp = await firstValueFrom(this.data_service.createTag(payload));
      if (resp.status !== 'success' || !resp.data) {
        this.alertService.error(resp.error?.message || 'Failed to create tag.');
        return;
      }

      this.upsertAvailableTag(resp.data);
      this.resetQuickTagInput();
      await this.attachTag(resp.data);
      this.alertService.success(`Tag "${resp.data.name}" created.`);
    } catch (err: any) {
      this.alertService.error(err?.message || 'Failed to create tag.');
    } finally {
      this.isTagMutating = false;
    }
  }

  openCreateTagDialog(): void {
    const nextSortOrder = this.getNextTagSortOrder();
    this.editingTagIndex = null;
    this.editingTagScope = null;
    this.tagDraftId = '';
    this.tagDraftRev = undefined;
    this.tagDraftName = this.quickTagInputText.trim();
    this.tagDraftType = this.defaultTagType;
    this.tagDraftSlug = this.slugify(this.tagDraftName);
    this.tagDraftColor = '#2563EB';
    this.tagDraftIcon = '';
    this.tagDraftParentTagId = '';
    this.tagDraftParentTag = '';
    this.tagDraftSortOrder = nextSortOrder;
    this.tagDraftIsActive = true;
    this.tagDialogVisible = true;
  }

  /** `index` is the tag's position in noteTags (Space tags); pass -1 for a private tag. */
  openEditTagDialog(tag: Tag, index: number): void {
    if (!this.canManageTag(tag)) {
      return;
    }
    this.editingTagIndex = index;
    this.editingTagScope = index < 0 ? TAG_SCOPE.PRIVATE : TAG_SCOPE.SPACE;
    this.tagDraftId = tag._id;
    this.tagDraftRev = tag._rev;
    this.tagDraftName = tag.name;
    this.tagDraftType = this.normalizeTagType(tag.tagType);
    this.tagDraftSlug = tag.slug;
    this.tagDraftColor = tag.color ?? '#2563EB';
    this.tagDraftIcon = tag.icon ?? '';
    this.tagDraftParentTagId = tag.parentTagId ?? '';
    this.tagDraftParentTag = this.availableTags.find((availableTag) => availableTag._id === tag.parentTagId) ?? '';
    this.tagDraftSortOrder = tag.sortOrder ?? (index + 1) * 10;
    this.tagDraftIsActive = tag.isActive;
    this.tagDialogVisible = true;
  }

  async saveTagDialog(): Promise<void> {
    const name = this.tagDraftName.trim();
    if (!name) {
      this.alertService.warn('Tag name is required.');
      return;
    }

    const slug = this.slugify(this.tagDraftSlug || name);
    const scope = this.editingTagScope ?? this.effectiveNewTagScope;
    const duplicate = this.availableTags.some(
      (tag) => tag._id !== this.tagDraftId && tag.slug === slug && isPrivateTag(tag) === (scope === TAG_SCOPE.PRIVATE),
    );
    if (duplicate) {
      this.alertService.warn(`Another ${scope === TAG_SCOPE.PRIVATE ? 'private' : 'Space'} tag already uses this slug.`);
      return;
    }
    if (!this.editingTagScope && scope === TAG_SCOPE.PRIVATE && !this.canChangePrivateTags) {
      this.alertService.warn(this.privateTagsBlockedReason);
      return;
    }

    const fallbackSortOrder = this.editingTagIndex !== null ? (this.editingTagIndex + 1) * 10 : this.getNextTagSortOrder();
    const payload = this.buildTagPayload({
      name,
      tagType: this.normalizeTagType(this.tagDraftType),
      slug,
      color: this.tagDraftColor?.trim() || '#2563EB',
      icon: this.tagDraftIcon.trim() || 'tag',
      parentTagId: this.tagDraftParentTagId,
      sortOrder: Number.isFinite(this.tagDraftSortOrder) ? Number(this.tagDraftSortOrder) : fallbackSortOrder,
      isActive: this.tagDraftIsActive,
    });

    const existingServerTag = this.availableTags.find((tag) => tag._id === this.tagDraftId);
    // The server routes by spaceId alone: a SPACE tag's own Space, or none for a PRIVATE tag.
    const spaceId = existingServerTag ? existingServerTag.spaceId : scope === TAG_SCOPE.SPACE ? this.tagSpaceId : null;
    if (spaceId && !isPrivateTag(existingServerTag ?? { scope })) {
      payload.spaceId = spaceId;
    }

    this.isTagMutating = true;
    try {
      const resp = existingServerTag
        ? await firstValueFrom(this.data_service.updateTag(existingServerTag._id, payload))
        : await firstValueFrom(this.data_service.createTag(payload));

      if (resp.status !== 'success' || !resp.data) {
        this.alertService.error(resp.error?.message || 'Failed to save tag.');
        return;
      }

      this.upsertAvailableTag(resp.data);
      if (isPrivateTag(resp.data)) {
        // Renaming/recolouring a private tag, or attaching a new one, never touches the shared Note.
        if (!existingServerTag) {
          await this.addPrivateTag(resp.data);
        }
        const updated = resp.data;
        this.noteTags = this.noteTags.map((t) => (t._id === updated._id ? updated : t));
        this.refreshPrivateNoteTags();
      } else {
        this.replaceAssociatedTag(resp.data, this.editingTagIndex);
        this.markContentChanged();
      }
      this.tagDialogVisible = false;
      this.resetQuickTagInput();
      this.alertService.success(existingServerTag ? 'Tag updated.' : 'Tag created.');
    } catch (err: any) {
      this.alertService.error(err?.message || 'Failed to save tag.');
    } finally {
      this.isTagMutating = false;
    }
  }

  detachTagAt(index: number): void {
    if (index < 0 || index >= this.noteTags.length) {
      return;
    }
    this.noteTags = this.noteTags.filter((_, idx) => idx !== index);
    this.syncTagsToForm();
    this.markContentChanged();
  }

  async deleteTagAt(index: number): Promise<void> {
    if (index < 0 || index >= this.noteTags.length) {
      return;
    }

    const tag = this.noteTags[index];
    this.isTagMutating = true;
    try {
      const resp = await firstValueFrom(this.data_service.deleteTag(tag._id, tag.spaceId ?? undefined));
      if (resp.status !== 'success') {
        this.alertService.error(resp.error?.message || 'Failed to delete tag.');
        return;
      }

      this.noteTags = this.noteTags.filter((_, idx) => idx !== index);
      this.removeFromTagCatalog(tag);
      this.syncTagsToForm();
      this.filteredTagSuggestions = this.filteredTagSuggestions.filter((suggestedTag) => suggestedTag._id !== tag._id);
      this.markContentChanged();
      this.tagDialogVisible = false;
      this.alertService.success(`Tag "${tag.name}" deleted.`);
    } catch (err: any) {
      this.alertService.error(err?.message || 'Failed to delete tag.');
    } finally {
      this.isTagMutating = false;
    }
  }

  /** Tag Details "Delete": the Space tag at editingTagIndex, or the private tag being edited. */
  async deleteEditingTag(): Promise<void> {
    if (this.editingTagScope !== TAG_SCOPE.PRIVATE) {
      if (this.editingTagIndex !== null) await this.deleteTagAt(this.editingTagIndex);
      return;
    }
    const tag = this.privateTagCatalog.find((t) => t._id === this.tagDraftId);
    if (!tag) return;
    this.isTagMutating = true;
    try {
      const resp = await firstValueFrom(this.data_service.deleteTag(tag._id));
      if (resp.status !== 'success') {
        this.alertService.error(resp.error?.message || 'Failed to delete tag.');
        return;
      }
      if (this.privateTagIds.includes(tag._id)) {
        await this.detachPrivateTag(tag);
      }
      this.removeFromTagCatalog(tag);
      this.refreshPrivateNoteTags();
      this.tagDialogVisible = false;
      this.alertService.success(`Tag "${tag.name}" deleted.`);
    } catch (err: any) {
      this.alertService.error(err?.message || 'Failed to delete tag.');
    } finally {
      this.isTagMutating = false;
    }
  }

  /** Removes a private tag from this note via the private-tag-assignment API (the Note is untouched). */
  async detachPrivateTag(tag: Tag): Promise<void> {
    await this.mutatePrivateTags('remove', tag);
  }

  private async addPrivateTag(tag: Tag): Promise<void> {
    await this.mutatePrivateTags('add', tag);
  }

  /** Optimistic add/remove of one private tag, rolled back if the server refuses it. Deliberately
   * doesn't mark the note changed or trigger an autosave: a private tag change is not a Note change. */
  private async mutatePrivateTags(op: 'add' | 'remove', tag: Tag): Promise<void> {
    if (!this.note) return;
    const has = this.privateTagIds.includes(tag._id);
    if ((op === 'add') === has) return;
    if (!this.canChangePrivateTags) {
      this.alertService.warn(this.privateTagsBlockedReason);
      return;
    }

    const previous = this.privateTagIds;
    this.privateTagIds = op === 'add' ? [...previous, tag._id] : previous.filter((id) => id !== tag._id);
    this.refreshPrivateNoteTags();

    const noteSpaceId = this.note.spaceId ?? null;
    const resp = await firstValueFrom(
      op === 'add'
        ? this.scopedTags.addPrivateTags(this.note._id, [tag._id], noteSpaceId)
        : this.scopedTags.removePrivateTags(this.note._id, [tag._id], noteSpaceId),
    );
    if (resp.status === 'success') {
      this.privateTagIds = resp.data ?? this.privateTagIds;
      this.noteTags = this.noteTags.filter((t) => !this.privateTagIds.includes(t._id));
    } else {
      this.privateTagIds = previous;
      this.alertService.error(resp.error?.message || 'Failed to update private tags.');
    }
    this.refreshPrivateNoteTags();
    this.cdRef.markForCheck();
  }

  /** Attaches a tag the way its scope requires. Returns true only if the shared Note changed (a Space
   * tag was added), which is also when it marks the note changed for autosave/Save. */
  private async attachTag(tag: Tag): Promise<boolean> {
    if (!hasCurrentTagScope(tag)) return false; // obsolete (unscoped) tag: never attached
    if (isPrivateTag(tag) && this.privateTagCatalog.some((t) => t._id === tag._id)) {
      await this.addPrivateTag(tag);
      return false;
    }
    const didAssociate = this.associateTag(tag);
    if (didAssociate) {
      this.markContentChanged();
    }
    return didAssociate;
  }

  private isTagOnNote(tag: Tag): boolean {
    return this.noteTags.some((t) => t._id === tag._id) || this.privateTagIds.includes(tag._id);
  }

  private findTagByName(name: string, scope: TagScope): Tag | undefined {
    const catalog = scope === TAG_SCOPE.PRIVATE ? this.privateTagCatalog : this.spaceTagCatalog;
    const slug = this.slugify(name);
    const lower = name.toLowerCase();
    return catalog.find((tag) => tag.slug === slug || tag.name.toLowerCase() === lower);
  }

  // ───── Move to Space (tasks/space-move-client.md) ─────────────────────────────────

  /** Spaces this note can be moved to: every Space the user can write to (the same list as the Space
   * field — moving needs edit rights at the destination) except the one the note is in. */
  get moveDestinationOptions(): Array<{ id: string; label: string }> {
    const current = this.note?.spaceId || this.privateSpaceId;
    return this.spaceSelectItems.filter((item) => item.id !== current);
  }

  /** Why the note can't be moved right now (UX only — the server re-checks everything). */
  get moveBlockedReason(): string | null {
    if (this.isSpaceEditable) return 'Save the note before moving it.';
    if (this.isContentChanged) return 'Save your changes before moving the note.';
    return this.data_service.moveBlockedReason(this.note);
  }

  openMoveToSpaceDialog(): void {
    const blocked = this.moveBlockedReason;
    if (blocked) {
      this.alertService.warn(blocked);
      return;
    }
    if (!this.moveDestinationOptions.length) {
      this.alertService.warn('There is no other Space you can move this note to.');
      return;
    }
    this.moveDestinationSpaceId = null;
    this.moveDialogVisible = true;
  }

  /** Calls the move endpoint. Not a Note save: last_modified is untouched and the note isn't marked
   * changed. On failure the note and the editor stay exactly as they were. */
  async confirmMoveToSpace(): Promise<void> {
    const destination = this.moveDestinationSpaceId;
    if (!this.note || !destination || this.isMoving) return;
    const blocked = this.moveBlockedReason;
    if (blocked) {
      this.alertService.warn(blocked);
      return;
    }

    this.isMoving = true;
    try {
      const resp = await firstValueFrom(this.data_service.moveNote(this.note, destination));
      if (resp.status !== 'success' || !resp.data) {
        this.alertService.error(resp.error?.message || 'Moving the note failed.');
        return;
      }
      const moved = resp.data;
      Object.assign(this.note, {
        _rev: moved._rev,
        remote_rev: moved.remote_rev,
        syncStatus: moved.syncStatus,
        spaceId: destination,
        Tags: [],
      });
      // Space tags don't move with the note; private tags do (they're keyed by note id).
      this.noteTags = [];
      this.syncTagsToForm();
      this.editNoteForm?.get('spaceId')?.setValue(destination, { emitEvent: false });
      void this.reloadSpaceTagCatalog();
      this.moveDialogVisible = false;
      const name = this.spaceSelectItems.find((item) => item.id === destination)?.label ?? 'the selected Space';
      this.alertService.success(`Moved to ${name}.`, { life: 4000 });
    } finally {
      this.isMoving = false;
      this.cdRef.markForCheck();
    }
  }

  /** The Space whose SPACE tags this note may use: the dropdown's value while it's still editable
   * (a new note), else the note's own, else (a note from before Spaces) the user's PRIVATE Space. */
  get tagSpaceId(): string | null {
    const selected = this.isSpaceEditable ? this.editNoteForm?.get('spaceId')?.value : null;
    return selected || this.note?.spaceId || this.privateSpaceId || null;
  }

  private get tagSpace(): Space | undefined {
    const id = this.tagSpaceId;
    return id ? this.spaceOptions.find((s) => s.id === id) : undefined;
  }

  /** UX only — the server enforces WRITE access on every Space-tag create/update/delete. Offered only in
   * a SHARED Space: in the user's own PRIVATE Space nobody else could see a "Space" tag anyway, so new
   * tags there are simply private (which also keeps them usable from every Space). */
  get canCreateSpaceTags(): boolean {
    const space = this.tagSpace;
    return (
      !!space &&
      space.type === 'SHARED' &&
      (space.current_user_role === 'OWNER' || space.current_user_role === 'ADMIN' || space.current_user_role === 'EDITOR')
    );
  }

  /** Scope a newly created tag gets: the user's explicit choice where a choice is offered, else PRIVATE. */
  get effectiveNewTagScope(): TagScope {
    return this.canCreateSpaceTags ? this.newTagScope : TAG_SCOPE.PRIVATE;
  }

  /** A private tag can always be managed by its owner (the only user who ever receives it). */
  canManageTag(tag: Tag): boolean {
    if (this.privateTagCatalog.some((t) => t._id === tag._id)) {
      return true;
    }
    return !isPrivateTag(tag) && this.canCreateSpaceTags;
  }

  /** Private tag assignments live only on the server (Phase 1 has no offline channel for them), and the
   * note must already exist there — a new note is kept local until its first explicit save. */
  get canChangePrivateTags(): boolean {
    return this.isOnline && !!this.note && !this.isSpaceEditable && !this.note.localDraft;
  }

  /** Whether the typed "Assign tag" name already exists in the scope a new tag would get (then Add
   * attaches it; otherwise it's created). */
  get quickTagExistsInScope(): boolean {
    const name = this.quickTagInputText.trim();
    return !!name && !!this.findTagByName(name, this.effectiveNewTagScope);
  }

  get privateTagsBlockedReason(): string {
    if (!this.isOnline) return 'Private tags can only be changed while online.';
    return 'Save the note first — private tags can be added once it is saved.';
  }

  /** The Space tags carried in Note.Tags (tag ids of the note's own Space only; see
   * resolveNoteSpaceTags). A Space tag that is also privately assigned shows once, as private. */
  private resolveSpaceNoteTags(note: Note): Tag[] {
    return resolveNoteSpaceTags(note?.Tags, this.spaceTagCatalog, {
      privateTagIds: [...this.privateTagCatalog.map((t) => t._id), ...this.privateTagIds],
      spaceId: note?.spaceId || this.privateSpaceId,
    });
  }

  /** privateNoteTags from privateTagIds. Ids the (loaded) private catalog doesn't know — e.g. a tag
   * deleted elsewhere — are hidden rather than shown as a raw id. */
  private refreshPrivateNoteTags(): void {
    const byId = new Map(this.privateTagCatalog.map((t) => [t._id, t] as const));
    this.privateNoteTags = this.sortTagsByOrder(
      this.privateTagIds.map((id) => byId.get(id)).filter((t): t is Tag => !!t),
    );
  }

  private rebuildTagCatalog(): void {
    this.spaceTagCatalog = this.sortTagsByOrder(this.spaceTagCatalog);
    this.privateTagCatalog = this.sortTagsByOrder(this.privateTagCatalog);
    this.availableTags = [...this.spaceTagCatalog, ...this.privateTagCatalog];
  }

  private removeFromTagCatalog(tag: Tag): void {
    this.spaceTagCatalog = this.spaceTagCatalog.filter((t) => t._id !== tag._id);
    this.privateTagCatalog = this.privateTagCatalog.filter((t) => t._id !== tag._id);
    if (!isPrivateTag(tag) && tag.spaceId) {
      this.scopedTags.removeCachedSpaceTag(tag.spaceId, tag._id);
    }
    this.rebuildTagCatalog();
    this.filteredTagSuggestions = this.filteredTagSuggestions.filter((suggestedTag) => suggestedTag._id !== tag._id);
    this.refreshTagSuggestions();
  }

  /** (Re)loads the SPACE tags of the note's Space — after load, once the user's PRIVATE Space id is
   * known, and when a new note's Space changes. */
  private async reloadSpaceTagCatalog(): Promise<void> {
    const spaceId = this.tagSpaceId;
    this.spaceTagCatalog = this.scopedTags.getCachedSpaceTags(spaceId);
    this.rebuildTagCatalog();
    try {
      const tags = await firstValueFrom(this.scopedTags.loadSpaceTags(spaceId));
      if (spaceId !== this.tagSpaceId) return; // Space changed meanwhile; a newer reload owns it
      this.spaceTagCatalog = tags;
      this.rebuildTagCatalog();
      if (this.note) {
        this.noteTags = this.resolveSpaceNoteTags(this.note);
      }
      this.refreshTagSuggestions();
      this.cdRef.markForCheck();
    } catch (err) {
      console.warn('Failed to load Space tags:', err);
    }
  }

  /** A new note's Space dropdown changed: its Space tags belong to the old Space, so they go. */
  onSpaceSelectionChanged(): void {
    const spaceId = this.tagSpaceId;
    const kept = this.noteTags.filter((tag) => tag.spaceId === spaceId);
    if (kept.length !== this.noteTags.length) {
      this.noteTags = kept;
      this.syncTagsToForm();
      this.alertService.info('Space tags from the previous Space were removed from this note.', { life: 4000 });
    }
    void this.reloadSpaceTagCatalog();
  }

  onTagNameDraftChanged(): void {
    this.tagDraftSlug = this.slugify(this.tagDraftName);
  }

  searchTagSuggestions(event: any): void {
    this.refreshTagSuggestions(event?.query);
  }

  onTagAutocompleteSelected(event: any): void {
    const tag = event?.value as Tag | undefined;
    if (!tag) {
      return;
    }

    this.resetQuickTagInput();
    void this.attachTag(tag);
  }

  showAvailableTagSuggestions(): void {
    this.refreshTagSuggestions();
  }

  searchParentTagSuggestions(event: any): void {
    const query = String(event?.query ?? '').trim().toLowerCase();
    const currentTagId = this.tagDraftId;

    this.filteredParentTagSuggestions = this.sortTagsByOrder(
      this.availableTags.filter((tag) => {
        if (currentTagId && tag._id === currentTagId) {
          return false;
        }

        if (!query) {
          return true;
        }

        return [tag.name, tag.slug, tag.tagType, tag.icon ?? '']
          .some((value) => value.toLowerCase().includes(query));
      }),
    );
  }

  onParentTagSelected(event: any): void {
    const tag = event?.value as Tag | undefined;
    this.tagDraftParentTagId = tag?._id ?? '';
  }

  onParentTagInputChanged(value: string | Tag): void {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      const match = this.availableTags.find(
        (tag) => tag._id !== this.tagDraftId && (tag.name.toLowerCase() === normalized || tag.slug === this.slugify(value)),
      );
      this.tagDraftParentTagId = match?._id ?? '';
      return;
    }

    this.tagDraftParentTagId = value?._id ?? '';
  }

  clearParentTag(): void {
    this.tagDraftParentTag = '';
    this.tagDraftParentTagId = '';
    this.filteredParentTagSuggestions = [];
  }

  get quickTagInputText(): string {
    if (typeof this.quickTagName === 'string') {
      return this.quickTagName;
    }
    return this.quickTagName?.name ?? '';
  }

  get parentTagInputText(): string {
    if (typeof this.tagDraftParentTag === 'string') {
      return this.tagDraftParentTag;
    }
    return this.tagDraftParentTag?.name ?? '';
  }

  getTagChipLabel(tag: Tag): string {
    return `${tag.name} · ${tag.tagType}${tag.isActive ? '' : ' · inactive'}`;
  }

  getTagChipStyle(tag: Tag): Record<string, string> {
    const borderColor = tag.color || '#94a3b8';
    return tag.isActive
      ? { borderColor, backgroundColor: `${borderColor}14`, color: '#0f172a' }
      : { borderColor: '#9ca3af', backgroundColor: '#f3f4f6', color: '#6b7280', opacity: '0.85' };
  }

  getTagStatusLabel(tag: Tag): string {
    return tag.isActive ? 'Active' : 'Inactive';
  }

  private syncTagsToForm(): void {
    this.editNoteForm?.patchValue(
      {
        Tags: this.noteTags.map((tag) => tag._id),
      },
      { emitEvent: false },
    );
  }

  /** Refreshes the tag catalog from the server after the note has already
   * rendered from cache, so opening a note never blocks on a live tags fetch. */
  private async refreshTagCatalogInBackground(): Promise<void> {
    const note = this.note;
    try {
      const [tagsResp, privateIds] = await Promise.all([
        firstValueFrom(this.data_service.getAllTags()),
        // A new note isn't on the server yet, so it can't have private tags there — only ones queued
        // for it (e.g. the calendar tag of a note created from the Calendar), assigned after its push.
        note && !this.isSpaceEditable
          ? firstValueFrom(this.scopedTags.loadPrivateTagIds(note._id, note.spaceId ?? null))
          : Promise.resolve<string[] | null>(note ? this.scopedTags.getPendingPrivateTagIds(note._id) : null),
      ]);
      if (note !== this.note) return;
      if (tagsResp.status === 'success' && tagsResp.data) {
        this.privateTagCatalog = filterPrivateTags(tagsResp.data);
        this.rebuildTagCatalog();
      }
      if (privateIds) {
        this.privateTagIds = privateIds;
      }
      if (this.note) {
        this.noteTags = this.resolveSpaceNoteTags(this.note);
      }
      this.refreshPrivateNoteTags();
      this.cdRef.markForCheck();
    } catch (err) {
      console.warn('Failed to refresh tag catalog:', err);
    }
    await this.reloadSpaceTagCatalog();
  }

  private sortTagsByOrder(tags: Tag[]): Tag[] {
    return [...tags].sort((a, b) => {
      const orderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return a.name.localeCompare(b.name);
    });
  }

  private getNextTagSortOrder(): number {
    const maxSortOrder = this.noteTags.reduce((max, tag) => Math.max(max, tag.sortOrder ?? 0), 0);
    return maxSortOrder + 10;
  }

  private associateTag(tag: Tag): boolean {
    if (this.noteTags.some((noteTag) => noteTag._id === tag._id || noteTag.slug === tag.slug)) {
      return false;
    }
    this.noteTags = this.sortTagsByOrder([...this.noteTags, tag]);
    this.syncTagsToForm();
    return true;
  }

  private replaceAssociatedTag(tag: Tag, index: number | null): void {
    if (index === null || index < 0 || index >= this.noteTags.length) {
      this.associateTag(tag);
      return;
    }

    const nextTags = [...this.noteTags];
    nextTags[index] = tag;
    this.noteTags = this.sortTagsByOrder(nextTags);
    this.syncTagsToForm();
  }

  private upsertAvailableTag(tag: Tag): void {
    const upsert = (list: Tag[]) => [...list.filter((existingTag) => existingTag._id !== tag._id), tag];
    if (isPrivateTag(tag)) {
      this.privateTagCatalog = upsert(this.privateTagCatalog);
    } else {
      this.spaceTagCatalog = upsert(this.spaceTagCatalog);
      this.scopedTags.upsertCachedSpaceTag(tag);
    }
    this.rebuildTagCatalog();
  }

  private buildTagPayload(tag: Partial<Tag>): TagMutationPayload {
    return {
      name: String(tag.name ?? '').trim(),
      tagType: this.normalizeTagType(tag.tagType),
      slug: this.slugify(String(tag.slug ?? tag.name ?? '')),
      color: tag.color?.trim() || '#2563EB',
      icon: tag.icon?.trim() || 'tag',
      parentTagId: typeof tag.parentTagId === 'string' && tag.parentTagId.trim() ? tag.parentTagId.trim() : null,
      sortOrder: typeof tag.sortOrder === 'number' ? tag.sortOrder : this.getNextTagSortOrder(),
      isActive: tag.isActive !== false,
    };
  }

  private normalizeTagType(value: unknown): string {
    const normalized = String(value ?? '').trim().toLowerCase();
    return this.tagTypeOptions.includes(normalized) ? normalized : this.defaultTagType;
  }

  /** Quick "Assign tag…" suggestions: active tags only (attaching an inactive/archived tag to a note
   * isn't a normal flow — it's still reachable, and reactivatable, from Manage tags). */
  private refreshTagSuggestions(query: unknown = ''): void {
    const normalizedQuery = String(query ?? '').trim().toLowerCase();
    const selectedTagIds = new Set([...this.noteTags.map((tag) => tag._id), ...this.privateTagIds]);
    const matches = (tag: Tag) => {
      if (!tag.isActive || selectedTagIds.has(tag._id)) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      return [tag.name, tag.slug, tag.tagType, tag.icon ?? '']
        .some((value) => value.toLowerCase().includes(normalizedQuery));
    };

    const spaceTags = this.sortTagsByOrder(this.spaceTagCatalog.filter(matches));
    const privateTags = this.sortTagsByOrder(this.privateTagCatalog.filter(matches));
    this.tagSuggestionGroups = groupTagsByScope(spaceTags, privateTags);
    this.filteredTagSuggestions = [...spaceTags, ...privateTags];
  }

  private resetQuickTagInput(): void {
    this.quickTagName = '';
    this.refreshTagSuggestions();
  }

  private slugify(value: string): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  private generateTagId(slug: string): string {
    const randomPart = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}${Math.floor(Math.random() * 100000)}`;
    return `tag:${slug || 'item'}:${randomPart}`;
  }

  validateNote(): boolean {
    if (this.note) {
      if (!this.note.NoteType || this.note.NoteType.trim() === '') {
        const errorMsg = 'Note Type must not be empty!';
        this.snackbarService.showErrorMessage(errorMsg);
        return false;
      }
      // Inline tasks: description, due date and owner (the editor outlines the offending tasks).
      // Autosave doesn't validate, so nothing typed is ever lost.
      const taskIssues = this.descriptionEditor?.validateInlineTasks() ?? [];
      if (taskIssues.length) {
        const more = taskIssues.length > 1 ? ` (${taskIssues.length - 1} more task problem${taskIssues.length > 2 ? 's' : ''})` : '';
        this.snackbarService.showErrorMessage(taskIssues[0].message + more);
        return false;
      }
      return true;
    } else {
      const errorMsg = 'No note loaded to validate!';
      this.snackbarService.showErrorMessage(errorMsg);
      return false;
    }
  }

  private showOfflineSaveWarning(onDismiss?: () => void): void {
    this.confirmationService.confirm({
      header: 'Offline Save',
      message: 'You are offline. This note was saved locally and will sync when connection is restored.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'OK',
      rejectVisible: false,
      closable: false,
      closeOnEscape: false,
      dismissableMask: false,
      blockScroll: true,
      accept: () => {
        if (onDismiss) {
          onDismiss();
        }
      },
    });
  }

  private showLocalSaveWarning(message: string, onDismiss?: () => void): void {
    this.confirmationService.confirm({
      header: 'Saved Locally',
      message,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'OK',
      rejectVisible: false,
      closable: false,
      closeOnEscape: false,
      dismissableMask: false,
      blockScroll: true,
      accept: () => {
        if (onDismiss) {
          onDismiss();
        }
      },
    });
  }

  private waitForNextPaint(): Promise<void> {
    return new Promise((resolve) => {
      requestAnimationFrame(() => resolve());
    });
  }

  /** True if the file-upload panel (when mounted) has additions, renames, or
   * deletions that haven't been sent to the server yet. */
  private hasPendingAttachmentChanges(): boolean {
    const entries = this.fileUploadComponent?.fileEntries;
    if (!entries) return false;
    return entries.some((e) => e.status === 'new' || e.status === 'renamed' || e.status === 'deleted');
  }

  /** Uploads/renames/deletes attachments via the mounted file-upload panel and
   * merges the results into this.note.Attachments. Returns an error message
   * on failure, or null on success (including when there was nothing to do,
   * e.g. the attachments panel was never expanded). */
  private attachmentFlushChain: Promise<unknown> = Promise.resolve();

  /** Serialized: a flush waits for any flush already in flight, so e.g. an immediate post-rename
   * flush and a Close/Ctrl+S right after it can't both send the same rename/delete (statuses only
   * flip back to 'saved' once the server answers). The queued run then sees what's still pending. */
  private flushPendingAttachments(trigger: string): Promise<string | null> {
    const run = this.attachmentFlushChain.then(() => this.runAttachmentFlush(trigger));
    this.attachmentFlushChain = run.catch(() => undefined);
    return run;
  }

  /**
   * (staged) from the file-upload panel: an attachment upload/rename/delete was staged locally.
   * Mark the note dirty — so the status shows "Unsaved" and the unsaved-changes guards cover the
   * pending change — and in autosave mode send renames/deletes right away instead of only on
   * Close/View/Ctrl+S. New uploads keep going out on those exits (as before), since the note itself
   * may still be a local-only draft. Offline, the change stays staged and goes out on a later flush.
   */
  onAttachmentsStaged(kind: 'upload' | 'rename' | 'delete'): void {
    console.log(`[Attachments] staged change received by editor (kind=${kind})`, {
      noteId: this.note?._id,
      autosaveEnabled: this.autosaveEnabled,
      isOnline: this.isOnline,
    });
    this.markContentChanged();
    if (!this.autosaveEnabled || kind === 'upload') return;
    if (!this.isOnline) {
      console.log(`[Attachments] offline — ${kind} stays staged until the next flush`);
      return;
    }
    void this.forceAutosaveIfDirty(`attachment-${kind}`);
  }

  /** Attachments whose image was removed from the note's text since it was last applied (see
   * applyChangesToNoteFromForm); their files are deleted by the next save. */
  private readonly removedImageAttachmentIds = new Set<string>();

  /**
   * Deletes the files of images removed from the text, so they don't linger as attachments nobody sees.
   * Kept: an attachment the note still refers to (the image again, e.g. after Undo, or a link to the
   * file) or that another note uses (an image copied there). Through the Attachments panel when it's
   * open (its entry is marked deleted and goes out with its other changes), else directly; offline or
   * on a failure it's retried by a later save.
   */
  private async deleteRemovedImageAttachments(): Promise<void> {
    if (!this.removedImageAttachmentIds.size || !this.note) return;
    const ids = [...this.removedImageAttachmentIds];
    this.removedImageAttachmentIds.clear();
    const dropFromNote = (id: string) => {
      if (Array.isArray(this.note.Attachments)) {
        this.note.Attachments = this.note.Attachments.filter((existing) => existing !== id);
      }
    };

    for (const id of ids) {
      if (isAttachmentReferencedInHtml(id, this.note.DescriptionHtml, this.note.CommentsHtml) || this.isAttachmentUsedByOtherNote(id)) {
        continue;
      }
      if (FileUploadService.isQueuedAttachmentId(id)) {
        // Never reached the server: drop the queued upload.
        this.fileUploadService?.discardQueuedAttachment(id);
        dropFromNote(id);
        continue;
      }
      const entry = this.fileUploadComponent?.fileEntries?.find((e) => e._id === id);
      if (entry) {
        if (entry.status === 'saved' || entry.status === 'renamed') {
          entry.status = 'deleted';
        }
        continue;
      }
      if (!this.isOnline || !this.fileUploadService) {
        this.removedImageAttachmentIds.add(id);
        continue;
      }
      const result = await firstValueFrom(this.fileUploadService.deleteAttachment(this.note._id, id, this.note.spaceId ?? undefined));
      if (result.status === 'success' || result.error?.code === 404) {
        console.log(`[Attachments] deleted the file of removed image ${id}`);
        dropFromNote(id);
      } else {
        console.warn(`[Attachments] could not delete the file of removed image ${id}:`, result.error?.message);
        this.removedImageAttachmentIds.add(id);
      }
    }
  }

  private isAttachmentUsedByOtherNote(attachmentId: string): boolean {
    return (this.data_service.allNotes ?? []).some(
      (other) =>
        other._id !== this.note?._id &&
        ((Array.isArray(other.Attachments) && other.Attachments.includes(attachmentId)) ||
          isAttachmentReferencedInHtml(attachmentId, other.DescriptionHtml, other.CommentsHtml)),
    );
  }

  private async runAttachmentFlush(trigger: string): Promise<string | null> {
    await this.deleteRemovedImageAttachments();
    const entries = this.fileUploadComponent?.fileEntries ?? [];
    console.log(`[Attachments] flush triggered (trigger=${trigger})`, {
      noteId: this.note?._id,
      spaceId: this.note?.spaceId,
      uploaderMounted: !!this.fileUploadComponent,
      renames: entries.filter((e) => e.status === 'renamed').map((e) => ({ attachmentId: e._id, newName: e.name })),
      deletes: entries.filter((e) => e.status === 'deleted').map((e) => ({ attachmentId: e._id, name: e.name })),
      uploads: entries.filter((e) => e.status === 'new').map((e) => e.name),
    });
    if (!this.fileUploadComponent) {
      console.warn(`[Attachments] flush (trigger=${trigger}): file-upload component is NOT mounted — nothing can be sent`);
    }
    const fileUploadResult = this.fileUploadComponent
      ? await firstValueFrom(this.fileUploadComponent.saveChanges())
      : { status: 'success' as const, data: { AttachmentsAdded: [], AttachmentsDeleted: [] } };

    console.log(`[Attachments] flush finished (trigger=${trigger})`, fileUploadResult);

    if (fileUploadResult.status !== 'success') {
      return 'error' in fileUploadResult && fileUploadResult.error?.message
        ? fileUploadResult.error.message
        : 'Error during attachment upload.';
    }

    const addedIds = fileUploadResult.data?.AttachmentsAdded ?? [];
    const deletedIds = fileUploadResult.data?.AttachmentsDeleted ?? [];

    if (!Array.isArray(this.note.Attachments)) {
      this.note.Attachments = [];
    }

    for (const id of addedIds) {
      if (!this.note.Attachments.includes(id)) {
        this.note.Attachments.push(id);
      }
    }

    this.note.Attachments = this.note.Attachments.filter((id) => !deletedIds.includes(id));

    return null;
  }

  async saveNote(closeForm: boolean): Promise<boolean> {
    if (this.isSaving) return false;
    this.applyChangesToNoteFromForm();
    if (!this.validateNote()) return false;

    this.isSaving = true;
    const saveChangeCounter = this.contentChangeCounter;
    await this.waitForNextPaint();

    try {
      // 1️⃣ Upload attachments
      const uploadError = await this.flushPendingAttachments(closeForm ? 'save-and-close' : 'save');
      if (uploadError) {
        this.gDS.showGlobalDialog('Save Failed', 'Could not save note', uploadError, DialogType.Error);
        return false;
      }

      // 2️⃣ SAVE NOTE — FIXED
      // An explicit save uploads the note, so it stops being a local-only draft (see isSpaceEditable).
      delete this.note.localDraft;
      const noteSaveResult = await firstValueFrom(this.data_service.updateNote(this.note));

      this.temporaryStatus = false;
      if (noteSaveResult.status !== 'error') {
        await this.lockSpaceField();
      }

      if (noteSaveResult.status === 'success') {
        if (noteSaveResult.data) {
          this.note = { ...this.note, ...noteSaveResult.data };
        }

        // Persist the _rev returned by the server so subsequent saves use the
        // update path (PUT) instead of retrying a create (POST → 409 conflict).
        if (noteSaveResult.data?._rev && !this.note._rev) {
          (this.note as any)._rev = noteSaveResult.data._rev;
        }

        this.alertService.success('Changes saved successfully!', {
          keepAfterRouteChange: true,
          life: 4000,
        });

        if (saveChangeCounter === this.contentChangeCounter) {
          this.editNoteForm.markAsPristine();
          this.setContentChanged(false);
          this.tempMessage = 'Saved successfully!';
        }

        if (closeForm) {
          this.navigationService.clearHistory();
          this.navigateHome(true);
        }

        return true;
      }

      if (noteSaveResult.status === 'warning') {
        const msg = noteSaveResult.error?.message || 'Saved locally. Remote sync is pending.';
        this.tempMessage = msg;
        this.editNoteForm.markAsPristine();
        this.setContentChanged(false);
        this.unsavedChangesDialogVisible = false;
        this.pendingCancelEdits = false;

        if (closeForm) {
          const onDismiss = () => {
            this.navigationService.clearHistory();
            this.navigateHome(true);
          };
          if (this.isOnline) {
            this.showLocalSaveWarning(msg, onDismiss);
          } else if (!this.offlineSaveDialogShown) {
            this.offlineSaveDialogShown = true;
            this.showOfflineSaveWarning(onDismiss);
          } else {
            this.alertService.warn('Note saved locally. Will sync when connection is restored.', { life: 5000 });
            onDismiss();
          }
        } else {
          if (this.isOnline) {
            this.showLocalSaveWarning(msg);
          } else if (!this.offlineSaveDialogShown) {
            this.offlineSaveDialogShown = true;
            this.showOfflineSaveWarning();
          } else {
            this.alertService.warn('Note saved locally. Will sync when connection is restored.', { life: 5000 });
          }
        }

        return true;
      }

      let msg = noteSaveResult.error?.message || 'An error occurred while saving the note.';
      // If error details, warnings, or excluded fields are present, show them
      if (noteSaveResult.error) {
        const err = noteSaveResult.error;
        if ((err as any).details) {
          msg += `\nDetails: ${JSON.stringify((err as any).details, null, 2)}`;
        }
        if ((err as any).warnings) {
          msg += `\nWarnings: ${JSON.stringify((err as any).warnings, null, 2)}`;
        }
        if ((err as any).excluded_fields) {
          msg += `\nExcluded fields: ${JSON.stringify((err as any).excluded_fields, null, 2)}`;
        }
      }
      this.gDS.showGlobalDialog('Save Failed', 'Could not save note', msg, DialogType.Error);
      return false;
    } catch (err: any) {
      console.error('❌ Error during saveNote:', err);
      const msg = err?.message || 'Error saving note.';
      this.gDS.showGlobalDialog('Save Failed', 'Could not save note', msg, DialogType.Error);
      return false;
    } finally {
      this.isSaving = false;
    }
  }

  private async saveNoteAndView(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    this.applyChangesToNoteFromForm();
    if (!this.validateNote()) {
      return;
    }

    this.isSaving = true;
    const saveChangeCounter = this.contentChangeCounter;
    await this.waitForNextPaint();

    try {
      const uploadError = await this.flushPendingAttachments('save-and-view');
      if (uploadError) {
        this.gDS.showGlobalDialog('Save Failed', 'Could not save note', uploadError, DialogType.Error);
        return;
      }

      // An explicit save uploads the note, so it stops being a local-only draft (see isSpaceEditable).
      delete this.note.localDraft;
      const noteSaveResult = await firstValueFrom(this.data_service.updateNote(this.note));

      this.temporaryStatus = false;
      if (noteSaveResult.status !== 'error') {
        await this.lockSpaceField();
      }

      if (noteSaveResult.status === 'success') {
        if (noteSaveResult.data) {
          this.note = { ...this.note, ...noteSaveResult.data };
        }

        if (noteSaveResult.data?._rev && !this.note._rev) {
          (this.note as any)._rev = noteSaveResult.data._rev;
        }

        this.alertService.success('Changes saved successfully!', {
          keepAfterRouteChange: true,
          life: 4000,
        });

        if (saveChangeCounter === this.contentChangeCounter) {
          this.editNoteForm.markAsPristine();
          this.setContentChanged(false);
          this.tempMessage = 'Saved successfully!';
        }

        this.router.navigate([this.viewRoute, this.note?._id], {
          state: {
            ...this.modeSwitchState(),
            filter: this.filter,
            homeState: this.homeState,
            note: this.note,
          },
        });
        return;
      }

      if (noteSaveResult.status === 'warning') {
        const msg = noteSaveResult.error?.message || 'Saved locally. Remote sync is pending.';
        this.tempMessage = msg;
        this.editNoteForm.markAsPristine();
        this.setContentChanged(false);
        this.unsavedChangesDialogVisible = false;
        this.pendingCancelEdits = false;

        const onDismiss = () => {
          this.router.navigate([this.viewRoute, this.note?._id], {
            state: {
              ...this.modeSwitchState(),
              filter: this.filter,
              homeState: this.homeState,
              note: this.note,
            },
          });
        };

        if (this.isOnline) {
          this.showLocalSaveWarning(msg, onDismiss);
        } else if (!this.offlineSaveDialogShown) {
          this.offlineSaveDialogShown = true;
          this.showOfflineSaveWarning(onDismiss);
        } else {
          this.alertService.warn('Note saved locally. Will sync when connection is restored.', { life: 5000 });
          onDismiss();
        }
        return;
      }

      const msg = noteSaveResult.error?.message || 'An error occurred while saving the note.';
      this.gDS.showGlobalDialog('Save Failed', 'Could not save note', msg, DialogType.Error);
    } catch (err: any) {
      console.error('❌ Error during saveNoteAndView:', err);
      const msg = err?.message || 'Error saving note.';
      this.gDS.showGlobalDialog('Save Failed', 'Could not save note', msg, DialogType.Error);
    } finally {
      this.isSaving = false;
    }
  }

  cancelEdits() {
    if (this.autosaveEnabled) {
      // Autosave keeps IndexedDB continuously up to date, so "Close" here
      // just needs to flush any still-debounced edit, not offer a discard
      // dialog — Stage 1 has no discard mechanism (that's autosave Stage 2).
      void this.forceSaveThenClose(true);
      return;
    }
    if (this.isContentChanged) {
      this.pendingCancelEdits = true;
      this.unsavedChangesDialogVisible = true;
      return;
    }
    this.completeCancelEdits();
  }

  private completeCancelEdits() {
    if (this.temporaryStatus == true) {
      if (this.note) {
        this.data_service.deleteNote(this.note).subscribe(
          // OBSOLETE Couch-DB specific version -- should delete locally only
          () => {
            console.log('HTTP DELETE Response:', Response);
            this.tempMessage = 'Deleted successfully!';
            this.snackbarService.showSuccessMessage('Successfully deleted temporary note!');
            this.navigationService.clearHistory();
            this.navigateHome();
          },
          (error) => {
            console.error('HTTP DELETE Error:', error);
            this.tempMessage = error.error.message || 'An error occurred while deleting the temporary note.';
            this.snackbarService.showErrorMessage(this.tempMessage);
          },
        );
      }
    }
    this.setContentChanged(false);
    this.navigationService.clearHistory();
    this.navigateHome();
  }

  /** Delete = Move to Trash, shared with View and the Home list (NoteActionsService). */
  deleteItem() {
    if (!this.note) return;
    this.noteActions.confirmAndDelete(
      this.note,
      () => {
        this.tempMessage = 'Moved to Trash';
        // Nothing left to save: the note is gone, so leaving must not prompt about unsaved changes.
        this.setContentChanged(false);
        this.navigationService.clearHistory();
        this.navigateHome(true);
      },
      this.confirmationService,
    );
  }

  async generateKeywords(): Promise<void> {
    console.log('in generateKeywords():');

    if (this.aiKeywordsLoading) {
      return;
    }
    this.aiKeywordsLoading = true;
    try {
      const formValue = this.editNoteForm.value;

      const payload = {
        title: formValue.Title ?? '',
        description: this.stripHtmlToText(formValue.DescriptionHtml ?? this.note?.DescriptionHtml ?? ''),
        comments: this.stripHtmlToText(formValue.CommentsHtml ?? this.note?.CommentsHtml ?? ''),
      };

      console.log('AI keyword payload:', payload);

      const resp = await firstValueFrom(this.data_service.generateKeywords(payload));
      if (resp.status === 'success') {
        const keywords = resp.data?.keywords ?? [];
        const keywordString = Array.isArray(keywords) ? keywords.join(', ') : '';
        this.editNoteForm.patchValue({ AiKeywords: keywordString });
        (this.note as any).AiKeywords = keywordString;
        this.alertService.success('AI keywords generated.');
      } else {
        const msg = resp.error?.message || 'Keyword generation failed.';
        this.alertService.warn(msg);
      }
    } catch (err: any) {
      const msg =
        err?.error?.error?.message || err?.error?.message || err?.message || 'Keyword generation request failed.';
      this.alertService.error(msg);
      console.error('Automatic keywords error:', err);
    } finally {
      this.aiKeywordsLoading = false;
    }
  }

  async generateSuggestedTags(): Promise<void> {
    if (this.aiSuggestTagsLoading) {
      return;
    }

    const noteId = this.note?._id;
    if (!noteId) {
      this.alertService.warn('Save this note before generating AI tag suggestions.');
      return;
    }

    this.aiSuggestTagsLoading = true;
    this.aiSuggestTagsDialogVisible = false;
    this.aiSuggestedTags = [];

    try {
      const payload = { note_id: noteId };

      const resp = await firstValueFrom(this.data_service.suggestTags(payload));
      if (resp.status !== 'success') {
        this.alertService.warn(resp.error?.message || 'AI tag suggestion failed.');
        return;
      }

      const suggestedTags = Array.isArray(resp.data?.suggested_tags) ? resp.data!.suggested_tags : [];
      this.aiSuggestedTags = suggestedTags.map((tag) => ({
        _id: String(tag._id ?? ''),
        name: String(tag.name ?? ''),
        description: String(tag.Description ?? ''),
        selected: true,
      })).filter((tag) => !!tag._id && !!tag.name);

      this.aiSuggestTagsDialogVisible = true;
      if (this.aiSuggestedTags.length === 0) {
        this.alertService.warn('No fitting tags were suggested for this note.');
      }
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || err?.message || 'AI tag suggestion request failed.';
      this.alertService.error(msg);
      console.error('AI suggest tags error:', err);
    } finally {
      this.aiSuggestTagsLoading = false;
    }
  }

  cancelSuggestedTagsDialog(): void {
    this.aiSuggestTagsDialogVisible = false;
    this.aiSuggestedTags = [];
  }

  async addSelectedSuggestedTags(): Promise<void> {
    const selected = this.aiSuggestedTags.filter((tag) => tag.selected);
    if (selected.length === 0) {
      this.alertService.warn('No tags selected.');
      return;
    }

    this.aiSuggestTagsDialogVisible = false;
    this.aiSuggestedTags = [];

    // One at a time: private tags are saved through the API as they're attached (attachTag() marks
    // the note changed itself, and only for Space tags).
    let addedCount = 0;
    for (const suggested of selected) {
      const resolvedTag = this.mapSuggestedTagToTag(suggested);
      if (this.isTagOnNote(resolvedTag)) {
        continue;
      }
      await this.attachTag(resolvedTag);
      if (this.isTagOnNote(resolvedTag)) {
        addedCount += 1;
      }
    }

    if (addedCount > 0) {
      this.alertService.success(`${addedCount} tag${addedCount === 1 ? '' : 's'} added.`);
      return;
    }

    this.alertService.warn('Selected tags are already assigned to this note.');
  }

  get hasSelectedSuggestedTags(): boolean {
    return this.aiSuggestedTags.some((tag) => tag.selected);
  }

  // ---------------------------------------------------------------
  // Related Notes
  // ---------------------------------------------------------------

  toggleRelatedNotes(): void {
    this.isRelatedNotesExpanded = !this.isRelatedNotesExpanded;
  }

  /** Moves the related note at `index` one position up (-1) or down (+1). */
  moveRelatedNote(index: number, direction: -1 | 1): void {
    const targetIndex = index + direction;
    if (index < 0 || index >= this.relatedNotes.length || targetIndex < 0 || targetIndex >= this.relatedNotes.length) {
      return;
    }

    const reordered = [...this.relatedNotes];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    this.relatedNotes = reordered;
    this.refreshRelatedNoteObjects();
    this.markContentChanged();
  }

  onNoteLinkInserted(noteId: string): void {
    if (!noteId || this.relatedNotes.includes(noteId)) {
      return;
    }
    this.relatedNotes = [...this.relatedNotes, noteId];
    this.refreshRelatedNoteObjects();
    this.markContentChanged();
  }

  openAddRelatedNoteDialog(): void {
    this.relatedNoteSearchQuery = '';
    this.relatedNoteSearchResults = [];
    this.addRelatedNoteCreateBacklink = true;
    this.addRelatedNoteDialogVisible = true;
  }

  onRelatedNoteSearch(): void {
    const q = this.relatedNoteSearchQuery.trim().toLowerCase();
    if (!q) {
      this.relatedNoteSearchResults = [];
      return;
    }
    this.relatedNoteSearchResults = this.data_service.allNotes
      .filter(
        (n) =>
          n._id !== this.note?._id &&
          (n.Title?.toLowerCase().includes(q) || n._id.toLowerCase().includes(q))
      )
      .slice(0, 25);
  }

  async selectRelatedNote(n: Note): Promise<void> {
    const id = n._id;
    if (!id) return;
    if (this.relatedNotes.includes(id)) {
      this.alertService.warn('This note is already linked.');
      return;
    }
    const createBacklink = this.addRelatedNoteCreateBacklink;
    const sourceId = this.note?._id;

    this.relatedNotes = [...this.relatedNotes, id];
    this.refreshRelatedNoteObjects();
    this.markContentChanged();
    this.addRelatedNoteDialogVisible = false;
    this.relatedNoteSearchQuery = '';
    this.relatedNoteSearchResults = [];

    if (createBacklink && sourceId) {
      await this.createBacklinksForSelectedNotes([id], sourceId);
    }
  }

  logRelatedNoteTrashClick(index: number): void {
    console.log('[RelatedNotes] native (click) fired on trash button, index=', index);
  }

  requestRemoveRelatedNote(index: number): void {
    console.log('[RelatedNotes] requestRemoveRelatedNote() called, index=', index);
    const ref = this.relatedNoteObjects[index];
    if (!ref) {
      console.log('[RelatedNotes] requestRemoveRelatedNote() aborted: no ref at index', index, this.relatedNoteObjects);
      return;
    }

    const targetNote = this.data_service.allNotes.find((n) => n._id === ref.id);
    const targetRelated = getOrderedRelatedNoteIds((targetNote as any)?.RelatedNotes);
    const hasBacklink = !!this.note?._id && targetRelated.includes(this.note._id);

    this.removeRelatedNotePendingIndex = index;
    this.removeRelatedNotePendingId = ref.id;
    this.removeRelatedNotePendingTitle = ref.title || '(Untitled)';
    this.removeRelatedNoteHasBacklink = hasBacklink;
    this.removeRelatedNoteDeleteBacklink = hasBacklink;
    this.removeRelatedNoteDialogVisible = true;
    console.log('[RelatedNotes] removeRelatedNoteDialogVisible set to true for', ref.id, ref.title);
  }

  async confirmRemoveRelatedNote(): Promise<void> {
    console.log('[RelatedNotes] confirmRemoveRelatedNote() called, pendingIndex=', this.removeRelatedNotePendingIndex, 'pendingId=', this.removeRelatedNotePendingId);
    this.removeRelatedNoteDialogVisible = false;

    const index = this.removeRelatedNotePendingIndex;
    const removedId = this.removeRelatedNotePendingId;
    const removedTitle = this.removeRelatedNotePendingTitle || '(Untitled)';
    const deleteBacklink = this.removeRelatedNoteDeleteBacklink && this.removeRelatedNoteHasBacklink;

    if (!this.note?._id || index < 0 || index >= this.relatedNotes.length) {
      console.log('[RelatedNotes] confirmRemoveRelatedNote() aborted early: note?._id=', this.note?._id, 'index=', index, 'relatedNotes.length=', this.relatedNotes.length);
      return;
    }

    const updatedRelatedNotes = this.relatedNotes.filter((_, i) => i !== index);

    try {
      // Persist the link removal on the CURRENT note immediately, rather than
      // deferring it to the next time the user clicks the main Save button.
      const updatedCurrentNote: Note = { ...this.note, RelatedNotes: toRelatedNotesPayload(updatedRelatedNotes) };
      const result = await firstValueFrom(this.data_service.updateNote(updatedCurrentNote));
      if (result.status === 'error') {
        throw new Error(result.error?.message || 'Failed to update note.');
      }

      // Reflect the server/IndexedDB-confirmed note and give visual confirmation
      // by removing the row from the Related Notes section.
      this.note = result.data ?? updatedCurrentNote;
      this.relatedNotes = updatedRelatedNotes;
      this.refreshRelatedNoteObjects();

      if (deleteBacklink && removedId) {
        try {
          const otherResult = await firstValueFrom(this.data_service.getNote(removedId));
          if (otherResult.status === 'success' && otherResult.data) {
            const otherNote = otherResult.data;
            const otherRelatedIds = getOrderedRelatedNoteIds((otherNote as any).RelatedNotes);

            if (otherRelatedIds.includes(this.note._id)) {
              const backlinkResult = await firstValueFrom(
                this.data_service.updateNote({
                  ...otherNote,
                  RelatedNotes: toRelatedNotesPayload(otherRelatedIds.filter((id) => id !== this.note!._id)),
                }),
              );
              if (backlinkResult.status === 'error') {
                throw new Error(backlinkResult.error?.message || 'Failed to remove backlink.');
              }
            }
          }
        } catch (backlinkErr: any) {
          // The primary link removal already succeeded; surface the backlink
          // failure on its own rather than rolling back the successful part.
          this.alertService.warn(
            `Removed "${removedTitle}" from Related Notes, but could not remove the backlink on the other note: ${backlinkErr?.message ?? backlinkErr}`,
          );
          return;
        }
      }

      this.alertService.success(`Removed "${removedTitle}" from Related Notes.`);
    } catch (err: any) {
      this.alertService.error(`Failed to remove "${removedTitle}" from Related Notes: ${err?.message ?? err}`);
    }
  }

  openFindSimilarForLink(): void {
    const noteId = this.note?._id;
    if (!noteId) {
      this.alertService.warn('Save the note first to find similar notes.');
      return;
    }
    this.similarForLinkResults = [];
    this.similarForLinkError = '';
    this.createBacklinksChecked = true;
    this.similarForLinkLoading = true;
    this.similarForLinkDialogVisible = true;

    this.data_service.findSimilarNotes(noteId).subscribe({
      next: (res) => {
        const raw = res?.data;
        const results: SimilarNoteResult[] = Array.isArray(raw)
          ? raw
          : Array.isArray((raw as any)?.results)
            ? (raw as any).results
            : [];
        if (results.length > 0) {
          console.log('[FindSimilar] first result keys:', Object.keys(results[0]));
          console.log('[FindSimilar] first result:', JSON.stringify(results[0]));
        }
        this.similarForLinkResults = results.map((r) => {
          const id = String(r._id ?? r.id ?? '');
          const localNote = id ? this.data_service.allNotes.find((n) => n._id === id) : undefined;
          return {
            ...r,
            Status: localNote?.Status ?? (r.Status as string | undefined),
            selected: false,
          };
        });
        this.similarForLinkLoading = false;
      },
      error: (err) => {
        console.error('Similar notes error:', err);
        this.similarForLinkError = 'Failed to load similar notes.';
        this.similarForLinkLoading = false;
      },
    });
  }

  async linkSelectedSimilarNotes(): Promise<void> {
    const selected = this.similarForLinkResults.filter((r) => r.selected);
    if (selected.length === 0) {
      this.alertService.warn('No notes selected.');
      return;
    }

    const backlinksRequested = this.createBacklinksChecked;
    const newlyLinkedIds: string[] = [];

    for (const item of selected) {
      const id = String(item._id ?? item.id ?? '');
      if (!id) continue;
      if (this.relatedNotes.includes(id)) continue;
      this.relatedNotes = [...this.relatedNotes, id];
      newlyLinkedIds.push(id);
    }

    if (newlyLinkedIds.length > 0) {
      this.refreshRelatedNoteObjects();
      this.markContentChanged();
      this.alertService.success(`${newlyLinkedIds.length} note${newlyLinkedIds.length === 1 ? '' : 's'} linked.`);
    } else {
      this.alertService.warn('Selected notes are already linked.');
    }

    this.similarForLinkDialogVisible = false;
    this.similarForLinkResults = [];

    if (backlinksRequested && this.note?._id && newlyLinkedIds.length > 0) {
      await this.createBacklinksForSelectedNotes(newlyLinkedIds, this.note._id);
    }
  }

  private async createBacklinksForSelectedNotes(targetIds: string[], sourceId: string): Promise<void> {
    for (const targetId of targetIds) {
      try {
        const otherNote = await this.fetchNoteById(targetId);
        if (!otherNote) continue;

        const otherRelatedIds = getOrderedRelatedNoteIds((otherNote as any).RelatedNotes);

        if (otherRelatedIds.includes(sourceId)) continue;

        await firstValueFrom(
          this.data_service.updateNote({
            ...otherNote,
            RelatedNotes: toRelatedNotesPayload([...otherRelatedIds, sourceId]),
          })
        );
      } catch (err) {
        console.warn(`[createBacklinks] Failed to create backlink in ${targetId}:`, err);
      }
    }
  }

  private async fetchNoteById(noteId: string): Promise<Note | undefined> {
    try {
      const response = await firstValueFrom(this.data_service.getNote(noteId));
      const note = response?.data;
      return note && typeof note === 'object' ? note : undefined;
    } catch (error) {
      console.error('Failed to load note for backlink creation:', noteId, error);
      return undefined;
    }
  }

  get hasSelectedSimilarForLink(): boolean {
    return this.similarForLinkResults.some((r) => r.selected);
  }

  getSimilarForLinkNoteId(item: SimilarNoteSelectionItem): string {
    return String(item._id ?? item.id ?? '');
  }

  formatSimilarForLinkDate(item: SimilarNoteSelectionItem): string {
    const raw = String(item.updated_at ?? item.modified_at ?? item.last_modified ?? item.modifiedDate ?? '');
    if (!raw) return '-';

    const d = new Date(raw);
    return isNaN(d.getTime())
      ? raw
      : d.toLocaleDateString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
  }

  /**
   * Cached, stable-reference projection of `relatedNotes` for the Related Notes
   * table. This MUST NOT be a getter: PrimeNG's p-table diffs rows by the
   * identity of the objects in `[value]`, and a getter re-invoked on every
   * Angular change-detection cycle would return brand-new object instances
   * each time, causing the table to tear down and rebuild every row on every
   * tick. That intermittently swaps out a button's DOM node between a click's
   * mousedown and mouseup, making clicks in the table silently fail. Call
   * refreshRelatedNoteObjects() explicitly whenever `relatedNotes` changes.
   */
  relatedNoteObjects: { id: string; title: string; lastModified: string; status: string; aiSummary: AiSummary | null }[] = [];

  private refreshRelatedNoteObjects(): void {
    this.relatedNoteObjects = this.relatedNotes.map((id) => {
      const note = this.data_service.allNotes.find((n) => n._id === id);
      return {
        id,
        title: note?.Title || '',
        lastModified: note?.last_modified || '',
        status: note?.Status || '',
        aiSummary: note?.ai_summary ?? null,
      };
    });
  }

  getAiSummaryTooltip(summary: AiSummary | null): string {
    if (!summary) return '';
    switch (summary.summary_status) {
      case 'current':
      case 'stale':
        return summary.summary_one_sentence || '';
      case 'pending':
        return 'AI summary is being generated…';
      case 'failed':
        return summary.summary_error ? `Summary failed: ${summary.summary_error}` : 'AI summary failed.';
      default:
        return '';
    }
  }

  // ---------------------------------------------------------------
  // AI Summary (read-only) — mirrors NoteViewBase's display
  // ---------------------------------------------------------------

  get aiSummary(): AiSummary | null {
    return this.note?.ai_summary ?? null;
  }

  get hasAiSummaryContent(): boolean {
    const summary = this.aiSummary;
    return !!(summary && (summary.summary_one_sentence || summary.summary_paragraph || summary.summary_bullets?.length));
  }

  get aiSummaryButtonLabel(): string {
    if (this.aiSummaryLoading) {
      return 'Generating…';
    }
    return this.hasAiSummaryContent ? 'Update summary' : 'Summarize now';
  }

  private static readonly AI_SUMMARY_POLL_INTERVAL_MS = 4000;
  private static readonly AI_SUMMARY_POLL_TIMEOUT_MS = 60000;

  async generateAiSummary(): Promise<void> {
    if (this.aiSummaryLoading) {
      return;
    }

    const noteId = this.note?._id;
    if (!noteId) {
      this.alertService.warn('Save this note before generating an AI summary.');
      return;
    }

    this.aiSummaryLoading = true;
    try {
      const resp = await firstValueFrom(this.data_service.regenerateAiSummary(noteId));
      if (resp.status !== 'success') {
        this.alertService.error(resp.error?.message || 'Failed to start AI summary generation.');
        return;
      }
      if (resp.data?.queued === false) {
        this.alertService.error('AI summary generation could not be queued. Please try again shortly.');
        return;
      }

      await this.pollAiSummaryStatus(noteId);
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || err?.message || 'AI summary request failed.';
      this.alertService.error(msg);
      console.error('AI summary error:', err);
    } finally {
      this.aiSummaryLoading = false;
    }
  }

  private async pollAiSummaryStatus(noteId: string): Promise<void> {
    const startedAt = Date.now();

    while (Date.now() - startedAt < NoteEditBase.AI_SUMMARY_POLL_TIMEOUT_MS) {
      await new Promise((resolve) => setTimeout(resolve, NoteEditBase.AI_SUMMARY_POLL_INTERVAL_MS));

      const statusResp = await firstValueFrom(this.data_service.getAiSummaryStatus(noteId));
      if (statusResp.status !== 'success' || !statusResp.data) {
        this.alertService.warn(statusResp.error?.message || 'Failed to check AI summary status.');
        return;
      }

      const data = statusResp.data;
      if (data.summary_status === 'current') {
        (this.note as any).ai_summary = {
          summary_status: 'current',
          summary_generated_at: data.summary_generated_at ?? undefined,
          summary_model: data.summary_model ?? undefined,
          summary_one_sentence: data.summary?.one_sentence,
          summary_paragraph: data.summary?.paragraph,
          summary_bullets: data.summary?.bullets,
          summary_keywords: data.summary?.keywords,
        };
        this.alertService.success('AI summary generated.');
        return;
      }

      if (data.summary_status === 'failed') {
        this.alertService.error(data.summary_error || 'AI summary generation failed.');
        return;
      }
      // pending/stale/none — keep polling until current, failed, or timeout.
    }

    this.alertService.warn('AI summary is still processing. Check back shortly.');
  }

  aiSummaryStatusIcon(status: string): string {
    switch (status) {
      case 'current':
      case 'stale':
        return 'info';
      case 'pending':
        return 'pending';
      case 'failed':
        return 'error_outline';
      case 'disabled':
        return 'block';
      default:
        return 'help';
    }
  }

  aiSummaryStatusIconClass(status: string): Record<string, boolean> {
    return {
      'edit-ai-summary-icon--stale': status === 'stale',
      'edit-ai-summary-icon--pending': status === 'pending' || status === 'disabled',
      'edit-ai-summary-icon--failed': status === 'failed',
    };
  }

  aiSummaryStatusLabel(status: string): string {
    switch (status) {
      case 'current':
        return 'Current';
      case 'stale':
        return 'Stale';
      case 'pending':
        return 'Pending';
      case 'failed':
        return 'Failed';
      case 'disabled':
        return 'Disabled';
      default:
        return status || 'Unknown';
    }
  }

  relatedNoteRowStyle(status: string): Record<string, string> {
    const isStrikethrough = isStruckStatus(status);
    const isMuted = isMutedStatus(status);
    return {
      'font-weight': isEmphasizedStatus(status) ? '700' : '400',
      'font-style': isMuted ? 'italic' : 'normal',
      color: isMuted ? 'var(--p-text-muted-color)' : 'var(--p-text-color)',
      'text-decoration': isStrikethrough ? 'line-through' : 'none',
    };
  }

  private stripHtmlToText(html: string): string {
    if (!html) return '';

    return html
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private mapSuggestedTagToTag(suggestedTag: SuggestedTagSelection): Tag {
    const existing = this.availableTags.find((tag) => tag._id === suggestedTag._id);

    if (existing) {
      return existing;
    }

    const fallbackTag: Tag = {
      _id: suggestedTag._id,
      type: 'tag',
      name: suggestedTag.name,
      description: suggestedTag.description || undefined,
      tagType: this.defaultTagType,
      slug: this.slugify(suggestedTag.name),
      color: '#2563EB',
      icon: 'tag',
      parentTagId: null,
      sortOrder: this.getNextTagSortOrder(),
      isActive: true,
    };

    this.upsertAvailableTag(fallbackTag);
    return fallbackTag;
  }

  async copyNoteId(): Promise<void> {
    const id = this.note?._id;

    if (!id) {
      this.snackbarService.showErrorMessage('No ID to copy.');
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(id);
        this.snackbarService.showSuccessMessage('ID copied to clipboard.');
        return;
      }

      this.fallbackCopyText(id);
      this.snackbarService.showSuccessMessage('ID copied to clipboard.');
    } catch (error) {
      console.error('Failed to copy note ID', error);
      this.snackbarService.showErrorMessage('Unable to copy ID.');
    }
  }

  private fallbackCopyText(text: string): void {
    const doc = this.elRef.nativeElement.ownerDocument as Document;
    const textarea = this.renderer.createElement('textarea') as HTMLTextAreaElement;

    this.renderer.setProperty(textarea, 'value', text);
    this.renderer.setStyle(textarea, 'position', 'fixed');
    this.renderer.setStyle(textarea, 'top', '-1000px');
    this.renderer.setStyle(textarea, 'left', '-1000px');
    this.renderer.setStyle(textarea, 'opacity', '0');

    this.renderer.appendChild(doc.body, textarea);
    textarea.select();
    doc.execCommand('copy');
    this.renderer.removeChild(doc.body, textarea);
  }
}
